import { Injectable, EventEmitter, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
// store
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { Subject } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { v4 as uuid4 } from 'uuid';

// services
import { ErrorHandler } from '../../shared/services/error-handler.service';
import { DataStorageService } from '../../shared/services/data-storage.service';
import { SharedService } from '../../shared/services/shared-service.service';
import { LoginService } from '../../shared/services/login.service';
import { AbandonmentService } from '../../shared/services/abandonment.service';
import { AppLoaderService } from '../../shared/services/app-loader.service';
import { BagItemsService } from '../../shared/services/bagItems.service';
import { EventsService } from './events.service';
import { PriceService } from './price.service';

// models
import { AppState } from '../models/app-state.interface';
import { BookingState, BookingJourneyItem, BookingFlightData, BookerItem, GenerateBookingReference } from '../models/booking.interface';
import { TimeslotBookingObject } from '../models/timeslot-reservation.interface';
import { CollectionLocation } from '../models/collection.interface';
import { DeliveryLocation } from '../models/delivery.interface';
import { BillingAddress, MakePaymentRequest } from '../models/payment.interface';
import { PaymentRequest } from '../models/payment-request.interface';
import { AmendRequest } from '../models/amend.interface';
// init models
import { initialBooking, initialBookingJourney, initialBookingFlightData } from '../models/booking-init.model';
import { initialMakePaymentRequest } from '../models/make-payment-init.model';
import { initialCollectionLocation, initialDeliveryLocation } from '../models/location-init.model';
// globals
import {
  API_PATH,
  SUBSCRIPTION_KEY,
  ADD_BOOKING,
  MAKE_PAYMENT,
  AMEND_BOOKING,
  GENERATE_BOOKING_REFERENCE,
  VALIDATE_PHONE_NUMBER,
  ISO_CURRENCY_CODES,
  GET_APPLE_PAY_SESSION,
  SLOT_NUMBER,
  GET_MAM_ACCESS_TOKEN,
  CHECK_FLY_BACK_ELIGIBILITY
} from '../../shared/globals';
import { DEFAULT_INTERPOLATION_CONFIG } from '@angular/compiler';
import moment from 'moment';
import { OldBookingInfo } from '../models/old-booking-infor.model';
// GA script
declare var ga: any;
declare var exponea: any;
declare var ThreedDS2Utils: any;
declare var libphonenumber: any;

const AMEND_MAPPING = {
  64: '',
  56: 'Bag, Location, Timeslot',
  48: 'Location, Timeslot',
  40: 'Bag, Location',
  24: 'Bag, Timeslot',
  16: 'Timeslot',
  8: 'Bag',
}

const eligibilityProducts = {
  0: 'None',
  2: 'ABC',
  3: 'CCD'
}

@Injectable({
  providedIn: 'root',
})
export class BookingService {

  public dataLinkVariableUpdated: Subject<boolean> = new Subject();
  public bookingInitFinished = false;
  public completeBookingRef = '';
  public _milePromotionStartDate = moment('2025-07-01');
  public _milesPromotionEndDate = moment('2025-07-31');
  public generateBooking$: Subject<any> = new Subject();
  public bookingPaymentError$: Subject<any> = new Subject();
  public manualFlightDataStored$: Subject<any> = new Subject();
  public getAllAirports$: Subject<any> = new BehaviorSubject<any>(null);
  public bookingStored$: Subject<Boolean> = new BehaviorSubject<Boolean>(false);
  public validPhoneNumberSubject$: Subject<any> = new Subject();
  public upsellBookingAdded$: Subject<any> = new Subject();
  public applePaymentComplete$: Subject<any> = new Subject();
  public expressCheckoutCommsPref$: Subject<any> = new Subject();
  // Emits when the customer closed the PayPal popup without completing payment.
  // requestStillPending is true when the booking call had not yet come back, in
  // which case the screen must stay blocked until it does.
  public paypalPopupAbandoned$: Subject<{ requestStillPending: boolean }> = new Subject();

  public currentBooking: BookingState = initialBooking;
  private _bookingOriginal = Object.assign({}, this.currentBooking);

  private _currentBookingJourney: BookingJourneyItem = initialBookingJourney;
  private _bookingJourneyOriginal = Object.assign({}, this._currentBookingJourney);

  private _currentBookingFlightData: BookingFlightData = initialBookingFlightData;
  private _bookingFlightOriginal = Object.assign({}, this._currentBookingFlightData);

  private _currentBookingDeliveryLocation: DeliveryLocation = initialDeliveryLocation;
  private _bookingDeliveryLocationOriginal = Object.assign({}, this._currentBookingDeliveryLocation);

  private _currentBookingCollectionLocation: CollectionLocation = initialCollectionLocation;
  private _bookingCollectionLocationOriginal = Object.assign({}, this._currentBookingCollectionLocation);

  private _currentBookingMakePayment: MakePaymentRequest = initialMakePaymentRequest;
  private _bookingMakePaymentOriginal = Object.assign({}, this._currentBookingMakePayment);

  // PayPal redirect popup.
  //
  // Lives on this (root-provided, session-long) service rather than on
  // StepPaymentComponent because a D2D booking is two ADD_BOOKING calls and
  // only the second one charges: goToBookingSwitcher() destroys the payment
  // component in between, which would take a component-held window reference
  // with it. Holding it here keeps the same Window object across the switch.
  private _paypalRedirectPopup: Window | null = null;
  // Set once the popup has been sent to PayPal, so failure cleanup can tell a
  // live redirect from a popup still showing its placeholder.
  private _paypalRedirectNavigated = false;
  // Set while a D2D booking is handing off from its first (non-paying) call to
  // its second (paying) one. The payment component is destroyed during that
  // switch and the popup must survive it; any OTHER destroy (the customer
  // navigating back to timeslots, etc.) should close the popup.
  private _paypalAwaitingD2dSecondLeg = false;
  private _paypalPopupWatch: any = null;
  // Whether a popup was successfully opened for the current attempt. Lets the
  // caller tell "blocked, never appeared" from "opened, then the customer
  // closed it" - only the former may fall back to a same-tab redirect.
  private _paypalPopupWasOpened = false;
  // Set when the customer closes the popup themselves. Survives until the next
  // submit so that a redirect response arriving afterwards is ignored instead of
  // hijacking the tab - they already cancelled.
  private _paypalPopupCancelled = false;

  private _postBookingSubscription: Subscription;
  private _makePaymentRequestSubscription: Subscription;
  private _removeBookingReferenceSubscription: Subscription;
  private _getBookingReferenceSubscription: Subscription;
  private _airportDataResponse;
  private _token = '';
  private _googlePaymentToken = '';
  private _applePaymentToken = '';
  private _oldPrice;
  private _amendBookingFlag = 0;
  private departureCountryCode;
  private bookingProduct;
  private arrivalCountryCode;

  constructor(
    private _appStore: Store<AppState>,
    private _http: HttpClient,
    private _router: Router,
    private _errorService: ErrorHandler,
    private _dataStorageService: DataStorageService,
    private _sharedService: SharedService,
    private _loginService: LoginService,
    private _abandonmentService: AbandonmentService,
    private _appLoaderService: AppLoaderService,
    private _bagItemsService: BagItemsService,
    private _eventsService: EventsService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this._removeBookingReferenceSubscription = this._errorService.removeBookingReference$
      .subscribe(removeRef => {
        this.updateBookingReference(null);
        this.updateBookingJourneyReference(null);
        this._appStore.dispatch({ type: 'UPDATE_BOOKING_REFERENCE', payload: '' });
        this._appStore.dispatch({ type: 'UPDATE_BOOKING_JOURNEY_REFERENCE', payload: '' });
        this._removeBookingReferenceSubscription.unsubscribe();
      });
  }

  updateDataVariable(val: any) {
    this.dataLinkVariableUpdated.next(val);
  }

  createAuthorizationHeader(headers: HttpHeaders, token) {
    headers = headers.set('Authorization', 'bearer ' + token);
    return headers;
  }

  // get store state
  getState(store: Store<AppState>): AppState {
    let state: AppState;
    store.pipe(take(1))
      .subscribe(s => state = s);
    return state;
  }


  // set get google payment token

  setGooglePaymentToken(token) {
    this._googlePaymentToken = token;
    this._appStore.dispatch({ type: 'GOOGLE_PAY_TOKEN', payload: token });
  }
  getGooglePaymentToken() {
    const state = this.getState(this._appStore);
    const d2dReducer = state.d2dReducer;
    return d2dReducer.googlePayToken;
  }

  setApplePaymentToken(token) {
    this._appStore.dispatch({ type: 'APPLE_PAY_TOKEN', payload: token });
  }
  getApplePaymentToken() {
    const state = this.getState(this._appStore);
    const bookingState = JSON.parse(JSON.stringify(state));
    const d2dReducer = bookingState.d2dReducer;

    return d2dReducer.applePayToken;
  }

  // Adyen's PayPal component hands back state.data (a JSON payload, not a
  // completed payment) via onSubmit - there is no separate authorize call.
  // Like applePayToken/googlePayToken, this is forwarded opaquely through
  // ADD_BOOKING and stringified before being stored, so the backend can
  // perform the actual Adyen /payments authorization as part of that one call.
  setPaypalPaymentToken(token: string) {
    this._appStore.dispatch({ type: 'PAYPAL_PAY_TOKEN', payload: token });
  }

  getPaypalPaymentToken(): string | null {
    const state = this.getState(this._appStore);
    const d2dReducer = state.d2dReducer;

    return d2dReducer.paypalPayToken;
  }

  setOldPriceService(oldprice) { this._oldPrice = oldprice; }
  getOldPriceService() { return this._oldPrice }


  // set get api token
  setApiToken(token) { this._token = token; }
  getApiToken() { return this._token; }

  // return all airport data
  getAirportData() {

    const returnedData: { hasError: boolean, data: any } = this._sharedService.getAirports();

    // airport data api call fallback
    // check if aiport data exists - called on server
    // if ok - pass data in to subject
    // if error - call api again, pass data back in callback - then pass in subject
    try {
      if (returnedData.hasError) {
        this.getAllAirports$.next('error');
        this._errorService.handleError(returnedData.data);
        this._appStore.dispatch({ type: 'AIRPORTS_LOADED', payload: false });
        return;
      } else {
        this._airportDataResponse = returnedData.data;
        this.getAllAirports$.next(returnedData.data);
        this._appStore.dispatch({ type: 'AIRPORTS_LOADED', payload: true });
      }
    } catch (error) {
      this._sharedService.getAirports(true, (d) => {
        if (d.hasError) {
          this.getAllAirports$.next('error');
          this._errorService.handleError(d.data);
          this._appStore.dispatch({ type: 'AIRPORTS_LOADED', payload: false });
          return;
        } else {
          this._airportDataResponse = d.data;
          this.getAllAirports$.next(d.data);
          this._appStore.dispatch({ type: 'AIRPORTS_LOADED', payload: true });
        }
      });
    }

  }

  // create init booking object
  createBookingInit(bookingProduct) {

    this._appStore.dispatch({ type: 'CURRENT_BOOKING_LOADED', payload: false });
    const state: any = this.getState(this._appStore);


    this.currentBooking = Object.assign({}, this._bookingOriginal);

    const data = JSON.parse(JSON.stringify(this.currentBooking));
    // reset refund request to null on init
    data.refundRequest = null;
    this.currentBooking = data;

    this.bookingStored$.next(false);

    if (this.currentBooking.bookingJourney.length === 0) {
      data.bookingJourney.push(this._bookingJourneyOriginal);
      this.currentBooking = data;
    }
    data.bookingJourney[0].flightData = this._bookingFlightOriginal;

    data.bookingJourney[0].journeyDirection = null;
    data.bookingJourney[0].journeyDirection = state.bookingReducer.bookingDetails.journeyLegDetails[0].journeyDirection;

    data.bookingJourney[0].collectionDateTimeUTC = '';
    data.bookingJourney[0].deliveryDateTimeUTC = '';

    data.bookingJourney[0].collectionLocation = Object.assign({}, this._bookingCollectionLocationOriginal);
    data.bookingJourney[0].deliveryLocation = Object.assign({}, this._bookingDeliveryLocationOriginal);
    data.makePaymentRequest = Object.assign({}, this._bookingMakePaymentOriginal);

    data.bookingJourney[0].note.text = '';

    data.bookingCompletion.collectionLocationDetails = false;
    data.bookingCompletion.collectionDateTimeDetails = false;
    data.bookingCompletion.deliveryLocationDetail = false;
    data.bookingCompletion.deliveryDateTimeDetails = false;
    data.bookingCompletion.bookingComplete = false;

    this.currentBooking = data;

    // console.log(this.getState(this._appStore).flightReducer.flightDetails.flightProducts.ccd, 'this.currentBooking');
    // console.log(this.getState(this._appStore).flightReducer.flightDetails.flightResponse.flightDirection, 'this.currentBooking');

    const ccd = this.getState(this._appStore).flightReducer.flightDetails.flightProducts.ccd;
    const flightDirection = this.getState(this._appStore).flightReducer.flightDetails.flightResponse.flightDirection;

    // const ccd = true;
    // const flightDirection = 2;
    console.log('bookingProduct', bookingProduct)
    if (bookingProduct === 'ccd') {
      this.updateBookingProductToCCD();
    } else if (bookingProduct === 'abc') {
      this.updateBookingProductToABC();
    }

    if (ccd && flightDirection === 2) {
      // rename to ccd
      this.updateBookingProductCCD();
    }

    // get booking reference
    this.getBookingReferenceForRequest();

    // get airport data from api
    this.getAirportData();
    this.updateBookingObject(this.currentBooking);

    // update add booking items
    // used to be done in old flight service
    this.updateCollectionOrDeliveryDateTime(state.flightReducer.flightDetails.flightResponse.productAvailability.journeyDirection, '');
    this.updateTimeslotReservation({ timeSlotReservationId: '', reservationEndTime: '', reservationStartTime: '', timeSlotType: null });
    if (state.flightReducer.flightDetails.flightResponse.productAvailability.journeyDirection === 1) {
      this.updateBookingFlagsCollectionDateTime(false);
    } else {
      this.updateBookingFlagsDeliveryDateTime(false);
    }

    this.updateVirtualBags(state.bookingReducer.bookingDetails.virtualBagCount);
    this.updateBagItems(state.bookingReducer.bookingDetails.bagItems);

    data.dataLinkSessionId = this.updateSessionId();
    data.returnSessionId = this.updateReturnJourneyId();

    const selectedMultiLegFlight = JSON.parse(JSON.stringify(this.getState(this._appStore).flightFinderReducer.selectedMultiLegFlight));

    const uuid = uuid4();
    this._appStore.dispatch({ type: 'SAVE_UUID', payload: uuid });
    // if ((it is actually multileg flight || user clicks on 1+stop) and we have flight details) {
    //  code logic
    //  changes done for 9488 & 9651
    // }
    if ((selectedMultiLegFlight?.flightStatusDetails?.length > 1 || state.flightFinderReducer.requestLegs > 1) && selectedMultiLegFlight && state.bookingReducer.bookingDetails.bookingType === 'new') {
      selectedMultiLegFlight.flightInfo.id = uuid;
    }

    data.bookingJourney[0].flightDetails = selectedMultiLegFlight;
    this.currentBooking = data;
    // if has return session
    // pass values into current booking object
    if (this.getState(this._appStore).flightFinderReducer.returnSessionString !== null) {

      const parsedData = JSON.parse(this.getState(this._appStore).flightFinderReducer.returnSessionString);

      this.updateVirtualBags(parsedData.numberOfBags);
      this.updateBagItems(parsedData.bagItems);
      this._appStore.dispatch({ type: 'UPDATE_VIRTUAL_BAGS', payload: parsedData.numberOfBags });
      this._appStore.dispatch({ type: 'UPDATE_BAG_ITEMS', payload: parsedData.bagItems });

      if (parsedData.journeyDirection === 1) {
        this.updateBookingFlagsCollectionLocation(true);
        this.updateCollectionOrDeliveryLocation(parsedData.journeyDirection, parsedData.collectionLocation);
      } else if (parsedData.journeyDirection === 2) {
        this.updateBookingFlagsDeliveryLocation(true);
        this.updateCollectionOrDeliveryLocation(parsedData.journeyDirection, parsedData.deliveryLocation);
      }

      if (parsedData.customer !== null) {
        // if current logged in user doesn't match return session user, logout
        const currUser = this.getState(this._appStore).userReducer;

        if (currUser.userDetails.userLoggedIn && (currUser.userDetails.currentUser.emailAddress !== parsedData.customer.emailAddress)) {
          this._loginService.logout();

          this.updateBooker({
            firstName: parsedData.customer.firstName,
            lastName: parsedData.customer.lastName,
            phoneNumber: parsedData.customer.phoneNumber,
            countryCode: parsedData.customer.countryCode,
            emailAddress: parsedData.customer.emailAddress
          });

          this._appStore.dispatch({ type: 'UPDATE_USER_EMAIL', payload: parsedData.customer.emailAddress });
        } else if (!currUser.userDetails.userLoggedIn) {
          try {
            this.updateBooker({
              firstName: parsedData.customer.firstName,
              lastName: parsedData.customer.lastName,
              phoneNumber: parsedData.customer.phoneNumber,
              countryCode: parsedData.customer.countryCode,
              emailAddress: parsedData.customer.emailAddress
            });
          } catch (error) {
            console.log('error adding in user details form return session');
          }
        }
      }

    }

    if (state.bookingReducer.bookingDetails.abTestingDataQuote.bookingPodEnv !== null && state.bookingReducer.bookingDetails.abTestingDataQuote.bookingPodVersion !== null) {
      const data = JSON.parse(JSON.stringify(this.currentBooking));
      data.bookingJourney[0].bookingPodEnv = state.bookingReducer.bookingDetails.abTestingDataQuote.bookingPodEnv;
      data.bookingJourney[0].bookingPodVersion = state.bookingReducer.bookingDetails.abTestingDataQuote.bookingPodVersion;
      this.currentBooking = data;
    }

    if (state.bookingReducer.bookingDetails.abTestingDataForm.bookingFormEnv !== null && state.bookingReducer.bookingDetails.abTestingDataForm.bookingFormVersion !== null) {
      const data = JSON.parse(JSON.stringify(this.currentBooking));
      data.bookingJourney[0].bookingFormEnv = state.bookingReducer.bookingDetails.abTestingDataForm.bookingFormEnv;
      data.bookingJourney[0].bookingFormVersion = state.bookingReducer.bookingDetails.abTestingDataForm.bookingFormVersion;
      this.currentBooking = data;
    }

    // check for referral (UTM) cookies
    const cookieUtmSource = this._dataStorageService.getCookie('airportr_original_partner_source');
    const cookieUtmCampaign = this._dataStorageService.getCookie('airportr_original_partner_campaign');
    const cookieUtmContent = this._dataStorageService.getCookie('airportr_original_partner_content');

    if (cookieUtmSource && cookieUtmCampaign && cookieUtmContent) {
      const data = JSON.parse(JSON.stringify(this.currentBooking));
      data.bookingJourney[0].utmSource = cookieUtmSource;
      data.bookingJourney[0].utmCampaign = cookieUtmCampaign;
      data.bookingJourney[0].utmContent = cookieUtmContent;
      this.currentBooking = data;
    }

    this.bookingInitFinished = true;
    this._appStore.dispatch({ type: 'CURRENT_BOOKING_LOADED', payload: this.bookingInitFinished });
  }
  // create init from stored data
  createBookingFromStore(booking: any) {
    const state: any = this.getState(this._appStore);
    const data = JSON.parse(JSON.stringify(booking.currentBooking));

    const flightData = state.flightReducer.flightDetails.flightResponse;
    const flightLeg = flightData.flightStatusDetails[0].flightLeg;
    const flightStatusDetails = flightData.flightStatusDetails[flightLeg - 1];
    const flightBookingData = {
      departureAirportCode: flightStatusDetails.departureAirport.airportCode,
      flightInfoId: flightData.flightInfo.id,
      requestFlightNumber: flightData.flightInfo.requestFlightNumber,
      airportCityName: flightStatusDetails.departureAirport.airportCityName,
      flightLeg: flightStatusDetails.flightLeg,
      requestDepartureDate: flightStatusDetails.departureDateTimeDetails.scheduledDateTimeLocal
    };
    data.bookingJourney[0].flightData = flightBookingData;




    const selectedMultiLegFlight = JSON.parse(JSON.stringify(this.getState(this._appStore).flightFinderReducer.selectedMultiLegFlight));

    const uuid = uuid4();
    this._appStore.dispatch({ type: 'SAVE_UUID', payload: uuid });
    if ((selectedMultiLegFlight?.flightStatusDetails?.length > 1 || state.flightFinderReducer.requestLegs > 1) && selectedMultiLegFlight) {
      selectedMultiLegFlight.flightInfo.id = uuid;
      data.bookingJourney[0].flightData.flightInfoId = uuid;
    }

    data.bookingJourney[0].flightDetails = selectedMultiLegFlight;
    this.currentBooking = data;

    data.bookingJourney[0].flightDetails = selectedMultiLegFlight;
    this.currentBooking = data;

    this._appStore.dispatch({ type: 'CURRENT_BOOKING_LOADED', payload: false });

    this.getAirportData();

    this.currentBooking = data;
    // if the currentBooking obj is missing the refundRequest Obj (old bookings)
    // add it in as null
    if (typeof this.currentBooking.refundRequest === 'undefined') {
      //this.currentBooking.refundRequest = null;
      const data = JSON.parse(JSON.stringify(this.currentBooking));
      data.refundRequest = null;
      this.currentBooking = data;
    }

    this.updateBookingObject(this.currentBooking);

    this.bookingInitFinished = true;
    this._appStore.dispatch({ type: 'CURRENT_BOOKING_LOADED', payload: this.bookingInitFinished });
  }

  updateBookingProductToCCD() {
    //this.currentBooking.bookingJourney[0].addOnProductType = [];
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingJourney[0].addOnProductType = [];
    data.bookingJourney[0].addOnProductType.push(2);
    this.currentBooking = data;
  }
  updateBookingProductToABC() {
    //this.currentBooking.bookingJourney[0].addOnProductType = [];
    //this.currentBooking.bookingJourney[0].addOnProductType.push(1);
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingJourney[0].addOnProductType = [];
    data.bookingJourney[0].addOnProductType.push(1);
    this.currentBooking = data;
  }
  updateBookingProductCCD() {
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingJourney[0].addOnProductType = [];
    data.bookingJourney[0].addOnProductType.push(2);
    this.currentBooking = data;
  }
  updateBookingDirection(direction) {
    this.currentBooking.bookingJourney[0].journeyDirection = direction;
  }
  updateSessionId() {
    const state = this.getState(this._appStore);
    return state.flightFinderReducer.dataLinkSession;
  }

  updateReturnJourneyId() {
    const state = this.getState(this._appStore);
    return state.flightFinderReducer.returnSessionId;
  }

  updateBookingFlight(objFlight: any) {
    //this.currentBooking.bookingJourney[0].flightData = objFlight;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingJourney[0].flightData = objFlight;
    this.currentBooking = data;
    this.updateBookingObject(this.currentBooking);
  }

  updateManualFlightData(manualFlight: any) {
    //this.currentBooking.bookingJourney[0].flightData = initialBookingFlightData;
    //this.currentBooking.bookingJourney[0].manualFlightData = manualFlight;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingJourney[0].flightData = initialBookingFlightData;
    data.bookingJourney[0].manualFlightData = manualFlight;
    this.currentBooking = data;
    this._appStore.dispatch({ type: 'UPDATE_MANUAL_DESTINATION', payload: manualFlight });
    this.updateBookingObject(this.currentBooking);
    this.manualFlightDataStored$.next(true);
  }

  updateAirportTime(direction: Number, value: any) {
    if (direction === 1) {
      //this.currentBooking.bookingJourney[0].deliveryDateTimeUTC = value;
      const data = JSON.parse(JSON.stringify(this.currentBooking));
      data.bookingJourney[0].deliveryDateTimeUTC = value;
      this.currentBooking = data;
    } else if (direction === 2) {
      //this.currentBooking.bookingJourney[0].collectionDateTimeUTC = value;
      const data = JSON.parse(JSON.stringify(this.currentBooking));
      data.bookingJourney[0].collectionDateTimeUTC = value;
      this.currentBooking = data;
    }
  }

  updateAirportLocation(journeyDirection: Number, code: string, terminal?: string) {

    let direction = '';

    if (terminal === null || typeof terminal === 'undefined') { terminal = ''; }
    if (code === 'LHR') { direction = '(Dep)'; }

    function filterByCode(obj) {
      if (obj.shortName.indexOf(code) !== -1 && obj.shortName.indexOf(terminal) !== -1 && obj.shortName.indexOf(direction) !== -1) {
        return obj;
      }
    }

    const filteredList = this._airportDataResponse.locations.filter(filterByCode);
    const airportDetails = filteredList[0];

    if (journeyDirection === 1) {
      //this.currentBooking.bookingJourney[0].deliveryLocation = airportDetails;
      const data = JSON.parse(JSON.stringify(this.currentBooking));
      data.bookingJourney[0].deliveryLocation = airportDetails;
      this.currentBooking = data;
    } else if (journeyDirection === 2) {
      //this.currentBooking.bookingJourney[0].collectionLocation = airportDetails;
      const data = JSON.parse(JSON.stringify(this.currentBooking));
      data.bookingJourney[0].collectionLocation = airportDetails;
      this.currentBooking = data;
    }
    //
    this._appStore.dispatch({ type: 'UPDATE_BOOKING_OBJECT', payload: this.currentBooking });
    this._appStore.dispatch({ type: journeyDirection === 1 ? 'UPDATE_OUT_BOUND_BOOKING_OBJECT' : 'UPDATE_IN_BOUND_BOOKING_OBJECT', payload: this.currentBooking })

  }

  updateCollectionOrDeliveryLocation(direction: Number, obj: any) {
    if (direction === 1) {
      //this.currentBooking.bookingJourney[0].collectionLocation = obj;
      const data = JSON.parse(JSON.stringify(this.currentBooking));
      data.bookingJourney[0].collectionLocation = obj;
      this.currentBooking = data;
    } else if (direction === 2) {
      //this.currentBooking.bookingJourney[0].deliveryLocation = obj;
      const data = JSON.parse(JSON.stringify(this.currentBooking));
      data.bookingJourney[0].deliveryLocation = obj;
      this.currentBooking = data;
    }
    this.updateBookingObject(this.currentBooking);
  }

  resetCollectionOrDeliveryLocation(direction: Number) {
    if (direction === 1) {
      // this.currentBooking.bookingJourney[0].collectionLocation = this._bookingCollectionLocationOriginal;
      const data = JSON.parse(JSON.stringify(this.currentBooking));
      data.bookingJourney[0].collectionLocation = this._bookingCollectionLocationOriginal;
      this.currentBooking = data;
      this.updateBookingFlagsCollectionLocation(false);
    } else if (direction === 2) {
      // this.currentBooking.bookingJourney[0].deliveryLocation = this._bookingDeliveryLocationOriginal;
      const data = JSON.parse(JSON.stringify(this.currentBooking));
      data.bookingJourney[0].deliveryLocation = this._bookingCollectionLocationOriginal;
      this.currentBooking = data;
      this.updateBookingFlagsDeliveryLocation(false);
    }
    this.updateBookingObject(this.currentBooking);
  }

  updateBookingFlagsCollectionLocation(val) {
    //this.currentBooking.bookingCompletion.collectionLocationDetails = val;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingCompletion.collectionLocationDetails = val;
    this.currentBooking = data;
    this.updateBookingObject(this.currentBooking);
  }
  updateBookingFlagsCollectionDateTime(val) {
    //this.currentBooking.bookingCompletion.collectionDateTimeDetails = val;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingCompletion.collectionDateTimeDetails = val;
    this.currentBooking = data;
    this.updateBookingObject(this.currentBooking);
  }
  updateBookingFlagsDeliveryLocation(val) {
    //this.currentBooking.bookingCompletion.deliveryLocationDetail = val;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingCompletion.deliveryLocationDetail = val;
    this.currentBooking = data;
    this.updateBookingObject(this.currentBooking);
  }
  updateBookingFlagsDeliveryDateTime(val) {
    //this.currentBooking.bookingCompletion.deliveryDateTimeDetails = val;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingCompletion.deliveryDateTimeDetails = val;
    this.currentBooking = data;
    this.updateBookingObject(this.currentBooking);
  }
  updateBookingFlagsBooking(val) {
    //this.currentBooking.bookingCompletion.bookingComplete = val;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingCompletion.bookingComplete = val;
    this.currentBooking = data;
    this.updateBookingObject(this.currentBooking);
  }

  updateTimeslotProductTierId(val: string) {
    // this.currentBooking.bookingJourney[0].productTierId = val;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingJourney[0].productTierId = val;
    this.currentBooking = data;
  }

  updateTimeslotId(val: string) {
    //this.currentBooking.bookingJourney[0].timeSlotId = val;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingJourney[0].timeSlotId = val;
    this.currentBooking = data;
  }

  updateTimeslotReservation(obj: TimeslotBookingObject) {
    //this.currentBooking.timeSlotReservation = obj;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.timeSlotReservation = obj;
    this.currentBooking = data;
    this.updateBookingObject(this.currentBooking);
  }

  updateCollectionOrDeliveryDateTime(direction: Number, date: string) {
    if (direction === 1) {
      //this.currentBooking.bookingJourney[0].collectionDateTimeUTC = date;
      const data = JSON.parse(JSON.stringify(this.currentBooking));
      data.bookingJourney[0].collectionDateTimeUTC = date;
      this.currentBooking = data;
    } else if (direction === 2) {
      //this.currentBooking.bookingJourney[0].deliveryDateTimeUTC = date;
      const data = JSON.parse(JSON.stringify(this.currentBooking));
      data.bookingJourney[0].collectionDateTimeUTC = date;
      this.currentBooking = data;
    }
    this.updateBookingObject(this.currentBooking);
  }

  resetTimeslotReservation() {
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.timeSlotReservation = {
      timeSlotReservationId: '',
      reservationStartTime: '',
      reservationEndTime: '',
      timeSlotType: null
    };
    this.currentBooking = data;
    this.updateBookingObject(this.currentBooking);
  }

  // swap delivery/collection data
  // for direction = 2
  updateDataAirportToLocation() {
    const newCollectionLocation = this.currentBooking.bookingJourney[0].deliveryLocation;
    const newCollectionDateTime = this.currentBooking.bookingJourney[0].deliveryDateTimeUTC;
    const newDeliveryLocation = this.currentBooking.bookingJourney[0].collectionLocation;
    const newDeliveryDateTime = this.currentBooking.bookingJourney[0].collectionDateTimeUTC;

    const data = JSON.parse(JSON.stringify(this.currentBooking));

    data.bookingJourney[0].collectionLocation = newCollectionLocation;
    data.bookingJourney[0].collectionDateTimeUTC = newCollectionDateTime;
    data.bookingJourney[0].deliveryLocation = newDeliveryLocation;
    data.bookingJourney[0].deliveryDateTimeUTC = newDeliveryDateTime;
    this.currentBooking = data;

    this.updateBookingObject(this.currentBooking);

  }

  updateBooker(item: BookerItem) {
    //this.currentBooking.bookingUser = data;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    this._appStore.dispatch({ type: 'BOOKER_DATA_BACKUP', payload: { ...data.bookingUser } })
    data.bookingUser = item;
    this.currentBooking = data;
    this.updateBookingObject(this.currentBooking);
  }


  validatePhoneNumber(number) {

    const request = {
      phoneNumber: number
    };

    // const headers = new HttpHeaders();
    // headers.append('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);
    const headers = new HttpHeaders()
      .set('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);


    this._http
      .post(API_PATH + VALIDATE_PHONE_NUMBER, request, { headers: headers })
      .subscribe(
        (res: any) => {
          this.validPhoneNumberSubject$.next(res.isValidPhoneNumber);
        },
        err => {
          this._errorService.handleError(err);
          return;
        });
  }

  updateNewsletterOptIn(val: Boolean) {
    //this.currentBooking.hasMarketingEnrolment = val;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.hasMarketingEnrolment = val;
    this.currentBooking = data;
  }

  updateCustomsForInspection(val: boolean) {
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.HasAirFranceGVACustomsAgreement = val;
    this.currentBooking = data;
  }

  updateDataSharingAllowed(val: Boolean) {
    //this.currentBooking.dataSharingAllowed = val;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.dataSharingAllowed = val;
    this.currentBooking = data;
  }

  updateSmsMarketingReminder(val: Boolean) {
    //this.currentBooking.smsMarketingReminder = val;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.smsMarketingReminder = val;
    this.currentBooking = data;
  }

  updateSmsWhatsAppEnrolment(val: Boolean) {
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.hasSmsWhatsAppEnrolment = val;
    this.currentBooking = data;
  }

  updateEmiratesSkywardsAcknowledgment(val: Boolean) {
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.hasEmiratesSkywardsAcknowledgment = val;
    this.currentBooking = data;
  }

  updateVirtualBags(value) {
    //this.currentBooking.bookingJourney[0].virtualBagCount = parseInt(value, 10);
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingJourney[0].virtualBagCount = parseInt(value, 10);
    this.currentBooking = data;
    this.updateBookingObject(this.currentBooking);
    // update d2d state
    setTimeout(() => {
      this.updateD2dstate();
    });
  }

  updateBagItems(items) {
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingJourney[0].virtualBagClassification = this._bagItemsService.bagItemsToBagsClassification(items);
    this.currentBooking = data;
    this.updateBookingObject(this.currentBooking);
  }

  updateBagNote(value) {
    //this.currentBooking.bookingJourney[0].note.text = value;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingJourney[0].note.text = value;
    this.currentBooking = data;
    this.updateBookingObject(this.currentBooking);

  }

  updateHasOversizedBag(value) {
    //this.currentBooking.bookingJourney[0].hasOversizedBag = value;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingJourney[0].hasOversizedBag = value;
    this.currentBooking = data;
    this.updateBookingObject(this.currentBooking);

  }

  getBookingReferenceForRequest() {

    // const headers = new HttpHeaders();
    // headers.append('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);
    const headers = new HttpHeaders()
      .set('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);

    const state = this.getState(this._appStore);
    const flightDirection = state.flightReducer.flightDetails.flightResponse.flightDirection;
    let flightDetails = flightDirection === 1 ? state.flightReducer.flightDetails.flightResponse.flightStatusDetails[0] : state.flightReducer.flightDetails.flightResponse.flightStatusDetails[state.flightReducer.flightDetails.flightResponse.flightStatusDetails.length - 1];

    if (state.flightFinderReducer.selectedMultiLegFlight) {
      flightDetails = flightDirection === 1 ? state.flightFinderReducer.selectedMultiLegFlight.flightStatusDetails[0] : state.flightFinderReducer.selectedMultiLegFlight.flightStatusDetails[state.flightFinderReducer.selectedMultiLegFlight.flightStatusDetails.length - 1];
    }

    const airlineCode = flightDetails.airlineCode
    const airportCode = flightDirection === 1 ? flightDetails.departureAirport.airportCode : flightDetails.arrivalAirport.airportCode

    const request: GenerateBookingReference = {
      addOnProductType: [flightDirection === 1 ? 'abc' : 'CarouselCollection'],
      airlineCode: airlineCode,
      airportCode: airportCode,
      preAcceptanceType: 'None',
    }

    if (state.hubDropReducer.selectedPostOffice && state.hubDropReducer.selectedCollectionType === 'atPostOffice') {
      request.preAcceptanceType = 'HubDrop';
    }

    // if we already have booking references use that instead
    const bookingReferenceState = state.bookingReducer.bookingDetails.bookingReferenceState;
    const { bookingReference: hubdropRef } = bookingReferenceState?.hubdropBookingReference || {};
    const { bookingReference: simpleRef } = bookingReferenceState?.simpleBookingReference || {};

    const isHubDrop = request.preAcceptanceType === 'HubDrop' && hubdropRef;
    const isNone = request.preAcceptanceType === 'None' && simpleRef;
    const isABCReference = isNone && simpleRef?.startsWith('AP1') && request.addOnProductType[0] === 'abc';
    const isCCDReference = isNone && simpleRef?.startsWith('AP2') && request.addOnProductType[0] === 'CarouselCollection';

    if (isHubDrop) {
      this.generateBookingReferenceResponseHandler(bookingReferenceState.hubdropBookingReference, request);
      return;
    } else if (isABCReference || isCCDReference) {
      this.generateBookingReferenceResponseHandler(bookingReferenceState.simpleBookingReference, request);
      return;
    }

    const promise = new Promise((resolve, reject) => {
      this._http
        .post(API_PATH + GENERATE_BOOKING_REFERENCE, request, { headers: headers })
        .toPromise().then((res: any) => {
          if (request.preAcceptanceType === 'HubDrop') {
            const bookingRefState = Object.assign({}, state.bookingReducer.bookingDetails.bookingReferenceState, { hubdropBookingReference: res });
            this._appStore.dispatch({ type: 'UPDATE_BOOKING_REFERENCE_STATE', payload: bookingRefState });
          } else {
            const bookingRefState = Object.assign({}, state.bookingReducer.bookingDetails.bookingReferenceState, { simpleBookingReference: res });
            this._appStore.dispatch({ type: 'UPDATE_BOOKING_REFERENCE_STATE', payload: bookingRefState });
          }

          this.generateBookingReferenceResponseHandler(res, request);
          resolve(res);
        }
        ).catch(err => {
          this._errorService.handleError(err);
          reject(err);
          return;
        });
    });

    return promise;
  }

  private generateBookingReferenceResponseHandler(res: any, request: GenerateBookingReference) {
    this.generateBooking$.next(true);
    this.updateBookingReference(res.bookingReference);
    this.updateBookingJourneyReference(res.bookingJourneyReference);
    this.updateUniqueSequenceIdentifier(res.uniqueSequenceIdentifier);

    this._appStore.dispatch({ type: 'UPDATE_BOOKING_REFERENCE', payload: res.bookingReference });
    this._appStore.dispatch({ type: 'UPDATE_BOOKING_JOURNEY_REFERENCE', payload: res.bookingJourneyReference });
    this._appStore.dispatch({ type: 'UPDATE_UNIQUE_SEQUENCE_ID', payload: res.uniqueSequenceIdentifier });

    this._appStore.dispatch({ type: 'UPDATE_USER_AGENT_BOOKING_REFERENCE', payload: res.bookingReference });

    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.generateBookingReferenceRequest = request;
    this.currentBooking = data;
    this._dataStorageService.saveData();
  }

  updateBookingReference(val: string) {
    //this.currentBooking.bookingReference = val;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingReference = val;
    this.currentBooking = data;
    this.updateBookingObject(this.currentBooking);
  }

  updateBookingJourneyReference(val: string) {
    //this.currentBooking.bookingReference = val;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingJourneyReference = val;
    this.currentBooking = data;
    this.updateBookingObject(this.currentBooking);
  }

  updateUniqueSequenceIdentifier(val: string) {
    //this.currentBooking.bookingReference = val;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.uniqueSequenceIdentifier = val;
    this.currentBooking = data;
    this.updateBookingObject(this.currentBooking);
  }

  updateBookingInstance(val: any) {
    const data = JSON.parse(JSON.stringify(val));
    this.currentBooking = data.currentBooking;
  }

  updatePayment(request: PaymentRequest, promoRequest: any, isCustomerService: boolean, billingAddress: BillingAddress, product: string, loggedIn: Boolean, retryRequest?: Boolean, type?: string) {

    const makePaymentRequest: MakePaymentRequest = Object.assign({}, this._bookingMakePaymentOriginal);
    const currentBookingDetails = this.getState(this._appStore).bookingReducer.bookingDetails;
    const isSbbSubsidyApplied = currentBookingDetails.isSbbSubsidyApplied;
    const isDbSubsidyApplied = currentBookingDetails.isDbSubsidyApplied || currentBookingDetails.isLhSubsidyApplied;
    const isCatDiscountApplied = currentBookingDetails.isCatDiscountApplied;

    let dbSubsidyType = currentBookingDetails.dbSubsidyType;
    if (dbSubsidyType === null) {
      if (currentBookingDetails.isDbSubsidyApplied) {
        dbSubsidyType = 1;
      } else if (currentBookingDetails.isLhSubsidyApplied) {
        dbSubsidyType = 0;
      }
    }

    makePaymentRequest.bookingReference = request.bookingReference;
    makePaymentRequest.bookingJourneyReference = '';
    makePaymentRequest.transactionType = request.transactionType;
    makePaymentRequest.businessTransactionType = request.businessTransactionType;
    makePaymentRequest.totalAmount.amount = request.fullAmount;
    makePaymentRequest.totalAmount.currency = request.currency;
    makePaymentRequest.isUserLoggedIn = !!this.getState(this._appStore).userReducer.userDetails?.userToken;

    const timeSlotData = this.getState(this._appStore).timeslotReducer;
    let lateBookingSurchargeAmount = 0;
    try {
      if (timeSlotData.timeslotFormattedData) {
        timeSlotData.timeslotFormattedData.timeSlotDates
          .find((date) => date.dateTime === timeSlotData.selectedDate).timeslotGroups
          ?.forEach((group) => {
            const timeslot = group.timeslots.find((timeslot) => timeslot.timeslotId === timeSlotData.selectedTimeslotId);

            if (timeslot) {
              lateBookingSurchargeAmount = timeslot.lateBookingSurchargePriceMajor || 0;
            }

          });
      }
    } catch (e) {
      console.error(e);
    }
    makePaymentRequest.lateBookingSurchargeAmount = lateBookingSurchargeAmount;

    // if bag type or bag bag count change
    const quoteBagsTypeCount = this.getState(this._appStore).flightFinderReducer.quoteFlightRequest?.bagItems;
    const quoteBagCount = this.getState(this._appStore).flightFinderReducer.quoteFlightRequest?.bagCount;
    const selectedBagTypeCount = this.getState(this._appStore).bookingReducer.bookingDetails.bagItems;
    if (quoteBagsTypeCount && (quoteBagsTypeCount.bags !== selectedBagTypeCount.bags || quoteBagsTypeCount.childEquipment !== selectedBagTypeCount.childEquipment || quoteBagsTypeCount.oversizeItem !== selectedBagTypeCount.oversizeItem)) {
      makePaymentRequest.isBagTypeChanged = true;
    }

    if (makePaymentRequest.isUserLoggedIn) {
      const loginType = this.getState(this._appStore).bookingReducer.bookingDetails.loginType;
      if (loginType) {
        makePaymentRequest.loginType = loginType;
      } else {
        makePaymentRequest.loginType = 'Already logged in';
      }
    } else {
      makePaymentRequest.loginType = '';
    }
    makePaymentRequest.funnelSource = this.getState(this._appStore).d2dReducer.entrySource || 'direct';
    const postOffice = this.getState(this._appStore).hubDropReducer.selectedPostOffice;
    if (postOffice) {
      makePaymentRequest.postOfficeName = postOffice.name;
      makePaymentRequest.postOfficeDistance = postOffice.distance;
    }

    if (quoteBagCount && quoteBagCount !== this.getState(this._appStore).bookingReducer.bookingDetails.virtualBagCount) {
      makePaymentRequest.isBagCountChanged = true;
    }

    // makePaymentRequest.googlePayment.googlePayToken = request.googlePayToken;

    if (request.applePayToken) {
      makePaymentRequest.applePayment = {
        applePayToken: request.applePayToken,
        amount: request.amount, // current booking price
        upsellBookingAmount: 0, // d2d booking price
      };
      makePaymentRequest.cardDetail = null;
    } else if (request.googlePayToken) {
      makePaymentRequest.googlePayment = {
        googlePayToken: request.googlePayToken,
        amount: request.amount, // current booking price
        upsellBookingAmount: 0, // d2d booking price
      };
      makePaymentRequest.cardDetail = null;
    } else if (request.paymenttype === 3) {
      makePaymentRequest.paypalPayment = {
        amount: request.amount,
        upsellBookingAmount: 0
      };
      makePaymentRequest.cardDetail = null;
    } else {
      makePaymentRequest.cardDetail.obscuredCardNumber = request.obscuredCardNumber;
      makePaymentRequest.cardDetail.encryptedCardInformation = request.encodedCardInformation;
      makePaymentRequest.cardDetail.amount = request.amount;
      makePaymentRequest.cardDetail.saveCard = request.saveCard;
    }

    if (makePaymentRequest.cardDetail?.amount === 0) {
      makePaymentRequest.cardDetail.obscuredCardNumber = '';
    }

    if (isSbbSubsidyApplied) {
      makePaymentRequest.sbbSubsidyAmount = this.getState(this._appStore).priceReducer.sbbSubsidyAmount;
    }

    if (isDbSubsidyApplied) {
      makePaymentRequest.dbSubsidyAmount = this.getState(this._appStore).priceReducer.dbSubsidyAmount;
      makePaymentRequest.dbSubsidyType = dbSubsidyType;
    }

    if (isCatDiscountApplied) {
      makePaymentRequest.CatDiscountAmount = this.getState(this._appStore).priceReducer.CatDiscountAmount;
    }

    const oversizeItemSurchange = this.getState(this._appStore).priceReducer.price.pricingResponse[0].pricingBreakdown.oversizeSurchargePriceMajor;
    makePaymentRequest.oversizeSurchargePrice = oversizeItemSurchange ? parseFloat((oversizeItemSurchange * 100).toFixed(2)) : 0;
    makePaymentRequest.lateBookingSurchargePrice = this.getState(this._appStore).priceReducer.price.pricingResponse[0].pricingBreakdown.lateBookingSurchargePriceMinor;
    this.updateHasOversizedBag(!!oversizeItemSurchange);

    if (request.recurringReference !== null && request.recurringReference !== 'newCard') {
      makePaymentRequest.recurringPayment.recurringDetailReference = request.recurringReference;
    } else {
      makePaymentRequest.recurringPayment = null;
    }

    makePaymentRequest.quoteId = request.quoteId;
    makePaymentRequest.transactionType = request.transactionType;
    makePaymentRequest.isPaidByInvoice = request.isPaidByInvoice;

    if (promoRequest) {
      makePaymentRequest.promotionRedemption = promoRequest;

    }

    makePaymentRequest.productTierId = this.currentBooking.bookingJourney[0].productTierId;
    makePaymentRequest.timeslotId = this.currentBooking.bookingJourney[0].timeSlotId;
    makePaymentRequest.flightDateTime = this.currentBooking.bookingJourney[0].flightData.requestDepartureDate;

    makePaymentRequest.isCustomerService = isCustomerService;

    makePaymentRequest.shopperInfo.billingAddress = billingAddress;

    const browserInf = ThreedDS2Utils.getBrowserInfo();
    makePaymentRequest.shopperInfo.browserInfo.acceptHeader = '';
    makePaymentRequest.shopperInfo.browserInfo.userAgent = browserInf.userAgent;
    makePaymentRequest.shopperInfo.browserInfo.language = browserInf.language;
    makePaymentRequest.shopperInfo.browserInfo.colorDepth = browserInf.colorDepth;
    makePaymentRequest.shopperInfo.browserInfo.screenHeight = browserInf.screenHeight;
    makePaymentRequest.shopperInfo.browserInfo.screenWidth = browserInf.screenWidth;
    makePaymentRequest.shopperInfo.browserInfo.timeZoneOffset = browserInf.timeZoneOffset;
    makePaymentRequest.shopperInfo.browserInfo.javaEnabled = browserInf.javaEnabled;
    makePaymentRequest.shopperInfo.IP = this.getState(this._appStore).userAgentReducer.ip;
    makePaymentRequest.shopperInfo.email = this.currentBooking.bookingUser.emailAddress;

    const milesReducer = this.getState(this._appStore).milesAndMoreReducer;
    if (milesReducer.isUserLoggedIn) {
      makePaymentRequest.milesInfo = {
        serviceCardNumber: milesReducer.miles.serviceCardNumber,
      }
      if (milesReducer.milesRedeemed) {
        const milesDiscount = this.getState(this._appStore).priceReducer.price.pricingResponse[0].pricingBreakdown.milesDiscountMinor;
        makePaymentRequest.milesInfo.transactionId = milesReducer.payByMilesResponse.transactionId;
        makePaymentRequest.milesInfo.currencyRatio = milesReducer.miles.currencyRatio;
        makePaymentRequest.milesInfo.milesDiscount = milesDiscount;
      }
    } else if (milesReducer.milesServiceCard) {
      makePaymentRequest.milesInfo = {
        serviceCardNumber: milesReducer.milesServiceCard,
      }
    }

    // this.currentBooking.makePaymentRequest = makePaymentRequest;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.makePaymentRequest = makePaymentRequest;
    this.currentBooking = data;

    if (promoRequest) {
      const updatedGenerateBookingReferenceRequest = { ...data.generateBookingReferenceRequest };
      updatedGenerateBookingReferenceRequest.promotionType = this.getState(this._appStore).priceReducer.price.promotionType;
      data.generateBookingReferenceRequest = updatedGenerateBookingReferenceRequest;
      this.currentBooking = data;
    }

    if (retryRequest) {
      this.postPaymentRequest(makePaymentRequest, loggedIn, product, type);
    } else {
      this.postBooking(loggedIn, product, request.applePayToken, request.googlePayToken, request.paymenttype === 3);
    }

  }

  updatePaymentForAmends(request: PaymentRequest, promoRequest: any, isCustomerService: boolean, billingAddress: BillingAddress, amendData: any) {
    const state = this.getState(this._appStore);
    const makePaymentRequest: MakePaymentRequest = Object.assign({}, this._bookingMakePaymentOriginal);
    const isSbbSubsidyApplied = state.bookingReducer.bookingDetails.isSbbSubsidyApplied;
    const isDbSubsidyApplied = state.bookingReducer.bookingDetails.isDbSubsidyApplied || state.bookingReducer.bookingDetails.isLhSubsidyApplied;
    const isCatDiscountApplied = state.bookingReducer.bookingDetails.isCatDiscountApplied;

    let dbSubsidyType = state.bookingReducer.bookingDetails.dbSubsidyType;
    if (dbSubsidyType === null) {
      if (state.bookingReducer.bookingDetails.isDbSubsidyApplied) {
        dbSubsidyType = 1;
      } else if (state.bookingReducer.bookingDetails.isLhSubsidyApplied) {
        dbSubsidyType = 0;
      }
    }

    makePaymentRequest.bookingReference = request.bookingReference;
    makePaymentRequest.bookingJourneyReference = '';
    makePaymentRequest.totalAmount.amount = request.fullAmount;
    makePaymentRequest.totalAmount.currency = request.currency;
    makePaymentRequest.totalAmount.baseCurrency = ISO_CURRENCY_CODES[state.priceReducer.price.totalPrice.baseCurrency];
    makePaymentRequest.totalAmount.exchangeRate = state.priceReducer.price.totalPrice.exchangeRate;
    makePaymentRequest.businessTransactionType = request.businessTransactionType;
    makePaymentRequest.isUserLoggedIn = !!this.getState(this._appStore).userReducer.userDetails?.userToken;
    const milesDiscount = state.priceReducer.price.pricingResponse[0].pricingBreakdown.milesDiscountMinor;
    if (milesDiscount > 0) {
      makePaymentRequest.totalAmount.amount = request.fullAmount + milesDiscount;
    }

    if (request.applePayToken) {
      makePaymentRequest.applePayment = {
        applePayToken: request.applePayToken,
        amount: request.amount, // current extra booking charge / refund
        upsellBookingAmount: 0, // d2d booking price
      };
      makePaymentRequest.cardDetail = null;
    } else if (request.googlePayToken) {
      makePaymentRequest.googlePayment = {
        googlePayToken: request.googlePayToken,
        amount: request.amount, // current extra booking charge / refund
        upsellBookingAmount: 0, // d2d booking price
      };
      makePaymentRequest.cardDetail = null;
    } else if (request.paymenttype === 3) {
      makePaymentRequest.paypalPayment = {
        amount: request.amount,
        upsellBookingAmount: 0
      };
      makePaymentRequest.cardDetail = null;
    } else {
      makePaymentRequest.cardDetail.obscuredCardNumber = request.obscuredCardNumber;
      makePaymentRequest.cardDetail.encryptedCardInformation = request.encodedCardInformation;
      makePaymentRequest.cardDetail.amount = request.amount;
      makePaymentRequest.cardDetail.saveCard = request.saveCard;
    }

    const attachedCard = this.getState(this._appStore).currentBookingReducer.currentBooking.makePaymentRequest?.cardDetail?.obscuredCardNumber
    if (makePaymentRequest.cardDetail?.amount === 0 && !attachedCard) {
      makePaymentRequest.cardDetail.obscuredCardNumber = '';
    }

    if (isSbbSubsidyApplied) {
      makePaymentRequest.sbbSubsidyAmount = state.priceReducer.sbbSubsidyAmount;
    }

    if (isDbSubsidyApplied) {
      makePaymentRequest.dbSubsidyAmount = state.priceReducer.dbSubsidyAmount;
      makePaymentRequest.dbSubsidyType = dbSubsidyType;
    }

    if (isCatDiscountApplied) {
      makePaymentRequest.CatDiscountAmount = state.priceReducer.CatDiscountAmount;
    }

    const oversizeItemSurchange = state.priceReducer.price.pricingResponse[0].pricingBreakdown.oversizeSurchargePriceMajor;
    makePaymentRequest.lateBookingSurchargePrice = this.getState(this._appStore).priceReducer.price.pricingResponse[0].pricingBreakdown.lateBookingSurchargePriceMinor;

    // check if oversize item surcharge for added in original booking
    const oversizeItems = this.getState(this._appStore).utilityReducer.bagItems.oversizeItem;

    if (!oversizeItems) {
      makePaymentRequest.oversizeSurchargePrice = oversizeItemSurchange ? oversizeItemSurchange * 100 : 0;
    } else {
      makePaymentRequest.oversizeSurchargePrice = 0;
    }

    this.updateHasOversizedBag(!!oversizeItemSurchange);

    if (request.recurringReference !== null && request.recurringReference !== 'newCard') {
      makePaymentRequest.recurringPayment.recurringDetailReference = request.recurringReference;
    } else {
      makePaymentRequest.recurringPayment = null;
    }

    // makePaymentRequest.googlePayment.googlePayToken = request.googlePayToken;
    makePaymentRequest.quoteId = request.quoteId;
    makePaymentRequest.transactionType = request.transactionType;
    makePaymentRequest.isPaidByInvoice = request.isPaidByInvoice;

    if (promoRequest) {
      makePaymentRequest.promotionRedemption = promoRequest;
    }

    makePaymentRequest.productTierId = state.currentBookingReducer.amendBooking.addBookingRequest.bookingJourney[0].productTierId;
    makePaymentRequest.timeslotId = state.currentBookingReducer.amendBooking.addBookingRequest.bookingJourney[0].timeSlotId;
    makePaymentRequest.flightDateTime = this.currentBooking.bookingJourney[0].flightData.requestDepartureDate;

    makePaymentRequest.isCustomerService = isCustomerService;

    makePaymentRequest.shopperInfo.billingAddress = billingAddress;

    const milesReducer = this.getState(this._appStore).milesAndMoreReducer;

    if (milesReducer.isUserLoggedIn) {
      makePaymentRequest.milesInfo = {
        serviceCardNumber: milesReducer.miles.serviceCardNumber,
      }
      if (milesReducer.milesRedeemed) {
        const milesDiscount = this.getState(this._appStore).priceReducer.price.pricingResponse[0].pricingBreakdown.milesDiscountMinor;
        makePaymentRequest.milesInfo.transactionId = milesReducer.payByMilesResponse.transactionId;
        makePaymentRequest.milesInfo.currencyRatio = milesReducer.miles.currencyRatio;
        makePaymentRequest.milesInfo.milesDiscount = milesDiscount;
      }
    }

    const browserInf = ThreedDS2Utils.getBrowserInfo();
    makePaymentRequest.shopperInfo.browserInfo.acceptHeader = '';
    makePaymentRequest.shopperInfo.browserInfo.userAgent = browserInf.userAgent;
    makePaymentRequest.shopperInfo.browserInfo.language = browserInf.language;
    makePaymentRequest.shopperInfo.browserInfo.colorDepth = browserInf.colorDepth;
    makePaymentRequest.shopperInfo.browserInfo.screenHeight = browserInf.screenHeight;
    makePaymentRequest.shopperInfo.browserInfo.screenWidth = browserInf.screenWidth;
    makePaymentRequest.shopperInfo.browserInfo.timeZoneOffset = browserInf.timeZoneOffset;
    makePaymentRequest.shopperInfo.browserInfo.javaEnabled = browserInf.javaEnabled;
    makePaymentRequest.shopperInfo.IP = state.userAgentReducer.ip;

    //this.currentBooking.makePaymentRequest = makePaymentRequest;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.makePaymentRequest = makePaymentRequest;
    this.currentBooking = data;
    this.postAmend(amendData, null);

  }

  updateMakePaymentProductTierId(val) {
    //this.currentBooking.makePaymentRequest.productTierId = val;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.makePaymentRequest.productTierId = val;
    this.currentBooking = data;
  }

  updateMakePaymentTimeslotId(val) {
    //this.currentBooking.makePaymentRequest.timeslotId = val;
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.makePaymentRequest.timeslotId = val;
    this.currentBooking = data;
  }

  updateHubdropBookingStatus(val: boolean) {
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.isHubdropBooking = val;
    this.currentBooking = data;
  }

  updateBookingObject(obj: any) {
    console.log('just about to update current booking reducer')
    this._appStore.dispatch({ type: 'UPDATE_BOOKING_OBJECT', payload: obj });
    if (obj.bookingJourney[0].journeyDirection === 1) {
      this._appStore.dispatch({ type: 'UPDATE_OUT_BOUND_BOOKING_OBJECT', payload: obj })
    } else {
      this._appStore.dispatch({ type: 'UPDATE_IN_BOUND_BOOKING_OBJECT', payload: obj })
    }
  }

  updateBookingZoneId(id: string) {
    const data = JSON.parse(JSON.stringify(this.currentBooking));
    data.bookingZoneId = id;
    this.currentBooking = data;
    this._appStore.dispatch({ type: 'UPDATE_BOOKING_OBJECT', payload: data });
  }

  updateD2dstate() {
    const state = this.getState(this._appStore);
    const bookingState = JSON.parse(JSON.stringify(state));
    const flightReducer = bookingState.flightReducer;
    const bookingReducer = bookingState.bookingReducer;
    const currentBookingReducer = bookingState.currentBookingReducer;
    const priceReducer = bookingState.priceReducer;
    const timeslotReducer = bookingState.timeslotReducer;
    const flightFinderReducer = bookingState.flightFinderReducer;
    const currentBookingType = bookingState.bookingReducer.bookingDetails.bookingProduct;
    const hubDropReducer = bookingState.hubDropReducer;
    const bookingType = currentBookingType === 'abc' ? 'outboundBooking' : 'inboundBooking'
    this._appStore.dispatch({ type: 'SAVE_BOOKING_DETAILS', payload: { bookingType: bookingType, flightReducer, bookingReducer, currentBookingReducer, priceReducer, timeslotReducer, flightFinderReducer, hubDropReducer, isCompleted: state.d2dReducer[bookingType].isCompleted } });
  }

  saveCutOffDateTime(paymentResponse: any) {
    // Persist the cut-off / grace-end date-time whenever the backend returns them,
    // regardless of isSuccessful / resultCode. Shown on the complete screen.
    if (paymentResponse && (paymentResponse.cutOffDateTime || paymentResponse.graceEndDateTime)) {
      this._appStore.dispatch({
        type: 'UPDATE_CUTOFF_DATETIME',
        payload: {
          cutOffDateTime: paymentResponse.cutOffDateTime,
          graceEndDateTime: paymentResponse.graceEndDateTime
        }
      });
    }
  }

  updateOldBookingstate() {
    const state = this.getState(this._appStore);
    const bookingState = JSON.parse(JSON.stringify(state));
    const flightReducer = bookingState.flightReducer;
    const bookingReducer = bookingState.bookingReducer;
    const currentBookingReducer = bookingState.currentBookingReducer;
    const priceReducer = bookingState.priceReducer;
    const timeslotReducer = bookingState.timeslotReducer;
    const flightFinderReducer = bookingState.flightFinderReducer;
    const currentBookingType = bookingState.bookingReducer.bookingDetails.bookingProduct;
    const hubDropReducer = bookingState.hubDropReducer;
    const bookingType = currentBookingType === 'abc' ? 'outboundBooking' : 'inboundBooking';

    // this._appStore.dispatch({ type: 'SAVE_OLD_BOOKINGS_DATA', payload: { abcBooking: abcBooking, ccdBooking: ccdBooking } });
  }

  getD2dDiscountPrice(price, milesPreview?, oldState?: OldBookingInfo): { outboundPrice: number; inboundPrice: number; totalD2dPrice: number } {
    let totalD2dPrice: any = 0;
    const state = oldState || this.getState(this._appStore);
    const bookingProduct = state.bookingReducer.bookingDetails.bookingProduct;
    const isEmbeddedPromo = !!state.flightFinderReducer.promoFromUrl;
    const milesRedeemed = state.milesAndMoreReducer.milesRedeemed;
    const d2d = state.d2dReducer;
    const milesReducer = state.milesAndMoreReducer;

    let inboundPrice = d2d.inboundBooking?.priceReducer?.price?.totalPrice?.majorAmount || 0;
    let outboundPrice = d2d.outboundBooking?.priceReducer?.price?.totalPrice?.majorAmount || 0;

    if (!state.bookingReducer.bookingDetails.isD2dService) {
      totalD2dPrice = price?.totalPrice?.majorAmount || 0;
      return { outboundPrice, inboundPrice, totalD2dPrice };
    }

    outboundPrice = +(outboundPrice.toFixed(2));
    inboundPrice = +(inboundPrice.toFixed(2));

    if (bookingProduct === 'abc') {
      outboundPrice = price?.totalPrice?.majorAmount || 0;
      totalD2dPrice = (price?.totalPrice?.majorAmount || 0) + inboundPrice;
    } else {
      inboundPrice = price?.totalPrice?.majorAmount;
      totalD2dPrice = (price?.totalPrice?.majorAmount || 0) + outboundPrice;
    }

    if (Object.keys(price).length === 0) {
      return { outboundPrice, inboundPrice, totalD2dPrice };
    }

    if (!isEmbeddedPromo && d2d.addBookingCount === 0) {
      if (price.promotionRewardType === 1) { // Fixed
        if (bookingProduct === 'abc') {
          const inboundBooking = state.d2dReducer.inboundBooking.priceReducer.price;
          inboundPrice = d2d.inboundBooking?.priceReducer?.price?.totalPrice?.majorAmount - price.discountAppliedMajor;
          // if promo is already applied from timeslot screen
          if (inboundBooking?.promoCodesApplied?.length > 0) {
            inboundPrice = d2d.inboundBooking?.priceReducer?.price?.totalPrice?.majorAmount + price.discountAppliedMajor;
          }
        } else {
          const outboundBooking = state.d2dReducer.outboundBooking.priceReducer.price;
          outboundPrice = d2d.outboundBooking?.priceReducer?.price?.totalPrice?.majorAmount - price.discountAppliedMajor;
          // if promo is already applied from timeslot screen
          if (outboundBooking?.promoCodesApplied?.length > 0) {
            outboundPrice = d2d.outboundBooking?.priceReducer?.price?.totalPrice?.majorAmount + price.discountAppliedMajor;
          }
        }
      }

      if (price.promotionRewardType === 2) { // Percentage
        const discountAmount = price.promotionRewardAmount / 100;

        if (bookingProduct === 'abc') {
          // if promo not already applied from timeslot screen
          const inboundBooking = state.d2dReducer.inboundBooking.priceReducer.price;
          if (typeof inboundBooking?.promoCodesApplied?.length === 'undefined' || inboundBooking?.promoCodesApplied?.length === 0) {
            inboundPrice = d2d.inboundBooking?.priceReducer?.price?.totalPrice?.majorAmount - (d2d.inboundBooking?.priceReducer?.price?.totalPrice?.majorAmount * discountAmount);
          }
        } else {
          // if promo not already applied from timeslot screen
          const outboundBooking = state.d2dReducer.outboundBooking.priceReducer.price;
          if (typeof outboundBooking?.promoCodesApplied?.length === 'undefined' || outboundBooking?.promoCodesApplied?.length === 0) {
            outboundPrice = d2d.outboundBooking?.priceReducer?.price?.totalPrice?.majorAmount - (d2d.outboundBooking?.priceReducer?.price?.totalPrice?.majorAmount * discountAmount);
          }
        }
      }
    }

    const milesDiscountedPrice = price.pricingResponse[0].pricingBreakdown.milesDiscountMajor
    if (!milesRedeemed && milesPreview) {
      inboundPrice -= (milesDiscountedPrice / 2);
      outboundPrice -= (milesDiscountedPrice / 2);
    }

    if (milesRedeemed && d2d.addBookingCount === 0) {
      if (bookingProduct === 'abc') {
        const submittedMiles = milesReducer.payByMilesResponse.ccdMilesSubmitted;
        const currencyRatio = milesReducer.miles.currencyRatio;
        const inboundMilesDiscountedPrice = +(submittedMiles / currencyRatio).toFixed(2);
        inboundPrice -= inboundMilesDiscountedPrice;
      } else {
        const submittedMiles = milesReducer.payByMilesResponse.abcMilesSubmitted;
        const currencyRatio = milesReducer.miles.currencyRatio;
        const inboundMilesDiscountedPrice = +(submittedMiles / currencyRatio).toFixed(2);
        outboundPrice -= inboundMilesDiscountedPrice;
      }
    }

    inboundPrice = +(+inboundPrice).toFixed(2);
    outboundPrice = +(+outboundPrice).toFixed(2);
    if (outboundPrice >= 0 && inboundPrice >= 0) {
      totalD2dPrice = inboundPrice + outboundPrice;
    }
    totalD2dPrice = +(+totalD2dPrice).toFixed(2);
    return { outboundPrice, inboundPrice, totalD2dPrice };
  }

  checkFlyBackEligibility() {
    const promise = new Promise((resolve, reject) => {
      const state = this.getState(this._appStore);

      const isD2d = state.bookingReducer.bookingDetails.isD2dService;
      const airlineCode = state.bookingReducer.bookingDetails.carrier.airlineCode;
      const isAbc = state.bookingReducer.bookingDetails.bookingProduct === 'abc';

      const flightDetails = state.flightReducer.flightDetails.flightResponse.flightStatusDetails;

      const departureAirportCode = flightDetails[flightDetails.length - 1].arrivalAirport.airportCode;
      const arrivalAirportCode = flightDetails[0].departureAirport.airportCode;


      let request = {
        airlineCode: airlineCode,
        departureAirportCode: departureAirportCode,
        arrivalAirportCode: arrivalAirportCode,
        primaryBooking: {
          product: isAbc ? 'ABC' : 'CCD',
          isD2D: false
        }
      }

      if (isD2d) {
        request.primaryBooking.isD2D = isD2d;
      }

      let headers = new HttpHeaders();
      headers = headers.set('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);
      this._http.post(API_PATH + CHECK_FLY_BACK_ELIGIBILITY, request, { headers: headers })
        .subscribe((res: any) => {
          const returnProducts = res?.flyBackProducts?.map(p => eligibilityProducts[p]) || [];
          this._appStore.dispatch({ type: 'UPDATE_FLYBACK_ELIGIBLE_PRODUCTS', payload: returnProducts });
          resolve(res);
        }, err => {
          console.log(err);
          resolve(err);
        });
    });

    return promise;
  }


  reCheckFlyBackEligibility() {
    const promise = new Promise((resolve, reject) => {
      const oldReducer = this.getState(this._appStore).oldBookingInfoReducer;
      const d2dReducer = oldReducer.d2dReducer;
      const currentBookingProduct = oldReducer.d2dReducer.currentBookingProduct;
      const oldReducerState = currentBookingProduct === "abc" ? d2dReducer.outboundBooking : d2dReducer.inboundBooking;

      const isD2d = oldReducerState.bookingReducer.bookingDetails.isD2dService;
      const airlineCode = oldReducerState.bookingReducer.bookingDetails.carrier.airlineCode;
      const isAbc = oldReducerState.bookingReducer.bookingDetails.bookingProduct === 'abc';

      const flightDetails = oldReducerState.flightReducer.flightDetails.flightResponse.flightStatusDetails;

      const departureAirportCode = flightDetails[flightDetails.length - 1].arrivalAirport.airportCode;
      const arrivalAirportCode = flightDetails[0].departureAirport.airportCode;


      let request = {
        airlineCode: airlineCode,
        departureAirportCode: departureAirportCode,
        arrivalAirportCode: arrivalAirportCode,
        primaryBooking: {
          product: isAbc ? 'ABC' : 'CCD',
          isD2D: false
        }
      }

      if (isD2d) {
        request.primaryBooking.isD2D = isD2d;
      }

      let headers = new HttpHeaders();
      headers = headers.set('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);
      this._http.post(API_PATH + CHECK_FLY_BACK_ELIGIBILITY, request, { headers: headers })
        .subscribe((res: any) => {
          const returnProducts = res?.flyBackProducts?.map(p => eligibilityProducts[p]) || [];
          this._appStore.dispatch({ type: 'UPDATE_FLYBACK_ELIGIBLE_PRODUCTS', payload: returnProducts });
          resolve(res);
        }, err => {
          console.log(err);
          resolve(err);
        });
    });

    return promise;
  }

  postPaymentRequest(request, isLoggedIn: Boolean, product: string, type?: string) {
    // const headers = new HttpHeaders();
    // headers.append('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);
    this.updateD2dstate();
    const state = this.getState(this._appStore);
    const flightData = state.flightReducer.flightDetails.flightResponse;
    this.bookingProduct = state.bookingReducer.bookingDetails.bookingProduct;
    this.departureCountryCode = flightData.flightStatusDetails[0].departureAirport.airportCountryCode;
    this.arrivalCountryCode = flightData.flightStatusDetails[flightData.flightStatusDetails.length - 1].arrivalAirport.airportCountryCode;
    let modifiedRequest = JSON.parse(JSON.stringify(request))
    // const currency = (this.departureCountryCode === 'CH' && this.bookingProduct === 'abc') || (this.arrivalCountryCode === 'CH' && this.bookingProduct === 'ccd') ? 'CHF' : 'GBP';
    const currency = this.getState(this._appStore).timeslotReducer.currency.code;
    modifiedRequest.totalAmount.currency = currency;
    modifiedRequest.totalAmount.exchangeRate = state.priceReducer.price.totalPrice.exchangeRate;
    modifiedRequest.totalAmount.baseCurrency = ISO_CURRENCY_CODES[state.priceReducer.price.totalPrice.baseCurrency];

    if (state.bookingReducer.bookingDetails.isD2dService && state.d2dReducer.addBookingCount === 1) {
      let upsellBookingReference;
      let upsellBookingAmount;
      let upsellQuoteId;

      if (state.bookingReducer.bookingDetails.bookingProduct === 'abc') {
        const upsellBooking = state.d2dReducer.inboundBooking;
        upsellBookingReference = upsellBooking.bookingReducer.bookingDetails.bookingReference;
        upsellBookingAmount = upsellBooking.priceReducer.price.totalPrice.minorAmount;
        upsellQuoteId = upsellBooking.priceReducer.price.quoteId;
      } else {
        const upsellBooking = state.d2dReducer.outboundBooking;
        upsellBookingReference = upsellBooking.bookingReducer.bookingDetails.bookingReference;
        upsellBookingAmount = upsellBooking.priceReducer.price.totalPrice.minorAmount;
        upsellQuoteId = upsellBooking.priceReducer.price.quoteId;
      }

      if (modifiedRequest.applePayment) {
        modifiedRequest.applePayment.upsellBookingAmount = upsellBookingAmount;
        modifiedRequest.cardDetail = null;
      } else if (modifiedRequest.googlePayment) {
        modifiedRequest.googlePayment.upsellBookingAmount = upsellBookingAmount;
        modifiedRequest.cardDetail = null;
      } else if (modifiedRequest.paypalPayment) {
        modifiedRequest.paypalPayment.upsellBookingAmount = upsellBookingAmount;
        modifiedRequest.cardDetail = null;
      } else {
        modifiedRequest.cardDetail.upsellBookingAmount = upsellBookingAmount;
      }

      modifiedRequest.upsellQuoteId = upsellQuoteId;
      modifiedRequest.isDoorToDoor = true;
    }

    let headers = new HttpHeaders();
    headers = headers.set('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);

    if (isLoggedIn) {
      headers = this.createAuthorizationHeader(headers, this._token);
    }
    this._makePaymentRequestSubscription = this._http
      .post(API_PATH + MAKE_PAYMENT, modifiedRequest, { headers: headers })
      .subscribe(
        (res: any) => {
          const bookingResponse = res;

          if (type === 'amend') {
            if (bookingResponse.paymentResponse.resultCode === 0 || bookingResponse.paymentResponse.resultCode === 1) {
              // 3DS authorized, complete booking
              this.handleMakePaymentSuccess(product, type);
              this._appStore.dispatch({ type: 'AMEND_PAYMENT_CONFIRMED', payload: true });
              return;
            }
          }
          if (type !== 'amend') {
            const abandonmentRequest = {
              email: this.currentBooking.bookingUser.emailAddress
            };

            this._appStore.dispatch({ type: 'PAYMENT_WITH_SAVED_CARD', payload: !!modifiedRequest.recurringPayment });
            this._abandonmentService.setAbandonment(abandonmentRequest, 'Unsubscribe');
            this.saveCutOffDateTime(bookingResponse.paymentResponse);

            setTimeout(() => {
              this.updateD2dstate();
            }, 30);

          }

          // if (product === 'abc') {
          //   ga('set', 'page', '/abc/complete');
          //   ga('send', 'pageview');
          // } else if (product === 'ccd') {
          //   ga('set', 'page', '/ccd/complete');
          //   ga('send', 'pageview');
          // }
          // this._router.navigate(['complete'], { queryParams: { ref: this.currentBooking.bookingReference } });
          if (bookingResponse.paymentResponse.resultCode === 0 || bookingResponse.paymentResponse.resultCode === 1) {
            // 3DS authorized, complete booking
            this.handleMakePaymentSuccess(product, type);

          } else if (bookingResponse.paymentResponse.resultCode === 7 && bookingResponse.paymentResponse.redirectShopperTokenResponse) {
            // For a card 3DS1 redirect the challenge overlay takes over the
            // screen, so the loader can go. For PayPal the payment continues in
            // the popup and this screen must stay blocked until it reports back
            // (hiding the loader also re-enables the submit button).
            if (!modifiedRequest.paypalPayment) {
              this._appLoaderService.hideLoader();
            }
            // 3DS1 Redirection (also used for PayPal's ResultCode =
            // RedirectShopper - same response shape, different rendering
            // downstream; see initiateChallengeShopper()/threeDS2Reducer).
            const data = {
              serverTransID: '',
              acsTransID: '',
              messageVersion: '',
              acsURL: '',
              paymentSessionId: bookingResponse.paymentResponse.redirectShopperTokenResponse.paymentSessionId,
              paymentRequest: bookingResponse.paymentResponse.redirectShopperTokenResponse.paymentRequest,
              termUrl: bookingResponse.paymentResponse.redirectShopperTokenResponse.termUrl,
              issueUrl: bookingResponse.paymentResponse.redirectShopperTokenResponse.issuerUrl,
              notificationUrl: bookingResponse.paymentResponse.redirectShopperTokenResponse.notificationUrl,
              resultCode: 7,
              isPaypal: !!modifiedRequest.paypalPayment
            };

            this.initiateChallengeShopper(data);

            // this._errorService.handleCustomError({ text: "Unfortunately we can't accept this card please try another card or call customer services. United Kingdom: +44 203 384 6677, Switzerland: +41 800 837 732", sticky: true });
          } else if (bookingResponse.paymentResponse.resultCode === 9) {
            // 3DS Identify Shopper
            const identifyRequest = {
              threeDSServerTransID: bookingResponse.paymentResponse.tokenResponse.serverTransID,
              threeDSMethodNotificationURL: bookingResponse.paymentResponse.tokenResponse.notificationURL
            };
            const encodedIdentifyRequest = ThreedDS2Utils.base64Url.encode(JSON.stringify(identifyRequest));

            const dataObj = {
              request: encodedIdentifyRequest,
              postURL: bookingResponse.paymentResponse.tokenResponse.notificationURL,
              originURL: bookingResponse.paymentResponse.tokenResponse.notificationURLOrigin,
              type: 'identify'
            };

            this._appStore.dispatch({ type: 'UPDATE_3DS_DATA', payload: dataObj });
          } else if (bookingResponse.paymentResponse.resultCode === 10) {
            // 3DS Challenge Shopper
            const data = {
              serverTransID: bookingResponse.paymentResponse.tokenResponse.serverTransID,
              acsTransID: bookingResponse.paymentResponse.tokenResponse.acsTransID,
              messageVersion: bookingResponse.paymentResponse.tokenResponse.messageVersion,
              acsURL: bookingResponse.paymentResponse.tokenResponse.acsURL
            };

            this.initiateChallengeShopper(data);
          } else if (this._paypalPopupCancelled) {
            // The customer cancelled the PayPal popup while this call was in
            // flight and the result is not one the branches above handle (e.g. a
            // refusal). Nothing else will release the screen.
            this._appLoaderService.hideLoader();
            this.paypalPopupAbandoned$.next({ requestStillPending: false });
          }
        },
        err => {
          this.exponeaPaymentErrorEvent(err);
          // The payment call failed outright, so any PayPal popup opened for
          // this attempt will never receive a redirect - close it rather than
          // leaving the customer on the placeholder.
          this.closePaypalPopup();
          this.bookingPaymentError$.next(true);
          this._errorService.handleError(err);
          return;
        }
      );

  }

  async postBooking(loggedIn: Boolean, product?: string, applePayToken?: any, googlePayToken?: any, isPaypal?: boolean) {
    console.log('add booking api call check token', googlePayToken, applePayToken, isPaypal)
    // console.log(this.currentBooking, this.getState(this._appStore).flightReducer.flightDetails, 'add booking and pay')
    const state = this.getState(this._appStore);
    const bookingState = JSON.parse(JSON.stringify(state));
    const bookingReducer = bookingState.bookingReducer;
    this.updateD2dstate();
    const ccd = state.flightReducer.flightDetails.flightProducts.ccd;
    const flightDirection = state.flightReducer.flightDetails.flightResponse.flightDirection;
    // const ccd = true;
    // const flightDirection = 2;
    // console.log(ccd, flightDirection);
    // console.log(this.currentBooking.bookingJourney[0].addOnProductType);
    // const headers = new HttpHeaders();
    // headers.append('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);
    const flightData = state.flightReducer.flightDetails.flightResponse;
    const hasCustomeAgreement = state.bookingReducer.bookingDetails.hasCustomsAgreement;
    this.departureCountryCode = flightData.flightStatusDetails[0].departureAirport.airportCountryCode;
    this.arrivalCountryCode = flightData.flightStatusDetails[flightData.flightStatusDetails.length - 1].arrivalAirport.airportCountryCode;
    this.bookingProduct = state.bookingReducer.bookingDetails.bookingProduct;
    // const currency = (this.departureCountryCode === 'CH' && this.bookingProduct === 'abc') || (this.arrivalCountryCode === 'CH' && this.bookingProduct === 'ccd') ? 'CHF' : 'GBP';
    const currency = state.timeslotReducer.currency.code;

    let deliverDateTime;
    let collectionDateTime;
    let modifiedRequest = JSON.parse(JSON.stringify(this.currentBooking));
    modifiedRequest.bookingzoneId = state.timeslotReducer.timeslotFormattedData.bookingZoneId;
    modifiedRequest.bookingJourney[0].isDomesticABC = state.d2dReducer.flightDetails.flightProducts.isDomesticABC;
    modifiedRequest.makePaymentRequest.totalAmount.currency = currency;
    modifiedRequest.makePaymentRequest.totalAmount.exchangeRate = state.priceReducer.price.totalPrice.exchangeRate;
    modifiedRequest.makePaymentRequest.totalAmount.baseCurrency = ISO_CURRENCY_CODES[state.priceReducer.price.totalPrice.baseCurrency];
    modifiedRequest.hasCustomsAgreement = hasCustomeAgreement;
    modifiedRequest.bookingJourney[0].languageCode = state.copyReducer.languageCode;

    if (flightDirection === 1 && state.hubDropReducer.selectedPostOffice && state.hubDropReducer.selectedCollectionType === 'atPostOffice') {
      modifiedRequest.bookingJourney[0].preAcceptanceType = 'HubDrop';
      modifiedRequest.bookingJourney[0].collectionLocation = this.formatCollectionPostLocation(state.hubDropReducer.selectedPostOffice);
      this._appStore.dispatch({ type: 'IS_BOOKING_HUBDROP_BOOKING', payload: true });
      this._appStore.dispatch({ type: 'SET_SELECTED_POST_OFFICE', payload: state.hubDropReducer.selectedPostOffice });
    }

    if (ccd && flightDirection === 2) {
      const flightLegs = state.flightReducer.flightDetails.flightResponse.flightStatusDetails
      collectionDateTime = flightLegs[flightLegs.length - 1].arrivalDateTimeDetails.scheduledDateTimeLocal;
      deliverDateTime = state.timeslotReducer.selectedTimeslotDateTime;

      modifiedRequest.bookingJourney[0].collectionDateTimeUTC = collectionDateTime;
      modifiedRequest.makePaymentRequest.flightDateTime = collectionDateTime;
      modifiedRequest.bookingJourney[0].addOnProductType = [2];

      if (deliverDateTime && !this.currentBooking.bookingJourney[0].deliveryDateTimeUTC) {
        modifiedRequest.bookingJourney[0].deliveryDateTimeUTC = deliverDateTime;
      }
    }

    // modify booking request if d2d Booking
    if (state.bookingReducer.bookingDetails.isD2dService && state.d2dReducer.addBookingCount === 0) {
      modifiedRequest.bookingJourney[0].isDoorToDoorBooking = true;
      // this._appStore.dispatch({ type: 'SAVE_FORM_DATA', payload:  modifiedRequest });
    }
    if (state.bookingReducer.bookingDetails.isD2dService && state.d2dReducer.addBookingCount === 1) {
      let upsellBookingReference;
      let upsellBookingAmount;
      let upsellQuoteId;

      if (state.bookingReducer.bookingDetails.bookingProduct === 'abc') {
        const upsellBooking = state.d2dReducer.inboundBooking;
        upsellBookingReference = upsellBooking.bookingReducer.bookingDetails.bookingReference;
        upsellBookingAmount = upsellBooking.priceReducer.price.totalPrice.minorAmount;
        upsellQuoteId = upsellBooking.priceReducer.price.quoteId;
      } else {
        const upsellBooking = state.d2dReducer.outboundBooking;
        upsellBookingReference = upsellBooking.bookingReducer.bookingDetails.bookingReference;
        upsellBookingAmount = upsellBooking.priceReducer.price.totalPrice.minorAmount;
        upsellQuoteId = upsellBooking.priceReducer.price.quoteId;
      }

      modifiedRequest.bookingJourney[0].upsellBookingReference = upsellBookingReference;
      modifiedRequest.bookingJourney[0].isDoorToDoorBooking = true;

      if (applePayToken) {
        modifiedRequest.makePaymentRequest.applePayment.upsellBookingAmount = upsellBookingAmount;
        modifiedRequest.makePaymentRequest.cardDetail = null;
      } else if (googlePayToken) {
        modifiedRequest.makePaymentRequest.googlePayment.upsellBookingAmount = upsellBookingAmount;
        modifiedRequest.makePaymentRequest.cardDetail = null;
      } else if (isPaypal) {
        modifiedRequest.makePaymentRequest.paypalPayment.upsellBookingAmount = upsellBookingAmount;
        modifiedRequest.makePaymentRequest.cardDetail = null;
      } else {
        modifiedRequest.makePaymentRequest.cardDetail.upsellBookingAmount = upsellBookingAmount;
      }

      modifiedRequest.makePaymentRequest.upsellQuoteId = upsellQuoteId;
      modifiedRequest.makePaymentRequest.isDoorToDoor = true;
      console.log({ 'currentQid': modifiedRequest.makePaymentRequest.quoteId, 'stateQid': state.priceReducer.price.quoteId });
    }

    if (state.utilityReducer.isDiamondAirline) {
      modifiedRequest.isDiamondAirBooking = true;
    }

    if (state.flybackReducer.flybackBooking) {
      const primaryBookingProduct = state.oldBookingInfoReducer.d2dReducer?.currentBookingProduct;
      if (primaryBookingProduct === 'abc') {
        modifiedRequest.bookingJourney[0].flybackLinkedBooking = state.oldBookingInfoReducer.abcBooking?.bookingReducer?.bookingDetails?.bookingReference;
      } else if (primaryBookingProduct === 'ccd') {
        modifiedRequest.bookingJourney[0].flybackLinkedBooking = state.oldBookingInfoReducer.ccdBooking?.bookingReducer?.bookingDetails?.bookingReference;
      }
    } else {
      try {
        const res: any = await this.checkFlyBackEligibility();
        const state = this.getState(this._appStore);
        const d2dReducer = state.d2dReducer;
        this._appStore.dispatch({
          type: 'SAVE_D2D_BOOKINGS_DATA', payload: {
            d2dReducer: d2dReducer,
            bookingReducer: state.bookingReducer,
            flightFinderReducer: state.flightFinderReducer,
            milesAndMoreReducer: state.milesAndMoreReducer,
          }
        });

        this._dataStorageService.saveData();

        if (res.flyBackProducts && res.flyBackProducts.length > 0) {
          modifiedRequest.bookingJourney[0].isEligibleForFlyBack = true;
        } else {
          modifiedRequest.bookingJourney[0].isEligibleForFlyBack = false;
        }
      } catch (e) {
        modifiedRequest.bookingJourney[0].isEligibleForFlyBack = false;
        console.log(e);
      }
    }

    let headers = new HttpHeaders();
    headers = headers.set('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);
    if (loggedIn) {
      headers = this.createAuthorizationHeader(headers, this._token);
    }

    modifiedRequest.slotNumber = this._sharedService.getCurrentSlotNumber();

    // get departure airport code
    let flightDetails = state.flightReducer.flightDetails.flightResponse.flightStatusDetails[0];
    if (state.flightFinderReducer.selectedMultiLegFlight) {
      flightDetails = state.flightFinderReducer.selectedMultiLegFlight.flightStatusDetails[0];
    }

    console.log('just before calling api', modifiedRequest);

    this._postBookingSubscription = this._http
      .post(API_PATH + ADD_BOOKING, modifiedRequest, { headers: headers })
      .subscribe(
        (res: any) => {
          const bookingResponse = res;

          if (res.bookingReference && res.bookingJourneyReference) {
            this.completeBookingRef = res.bookingReference;
            this.saveNewBookingReference(bookingResponse);
          }

          // incomplete booking
          if (bookingResponse.validationErrorMessages !== null) {
            this._appStore.dispatch({ type: 'UPDATE_ADD_BOOKING_CALLED', payload: true });
            return;
          } else if (bookingResponse.validationErrorMessages === null && bookingResponse.paymentResponse === null) {
            // this.upsellBookingAdded$.next(true);
            this._appStore.dispatch({ type: 'PAYMENT_WITH_SAVED_CARD', payload: !!modifiedRequest.makePaymentRequest.recurringPayment });
            this._appStore.dispatch({ type: 'UPDATE_CUSTOMER_RETURN_URL', payload: bookingResponse.returnJourneyURL });
            this._appStore.dispatch({ type: 'UPDATE_PNR_DATA', payload: bookingResponse.pnrBookingData });
            this._appStore.dispatch({ type: 'UPDATE_ADD_BOOKING_COUNT', payload: 1 });

            // The product switch below destroys the payment component. If a
            // PayPal popup is open it belongs to this booking and must survive
            // that destroy to receive the second call's redirect.
            this._paypalAwaitingD2dSecondLeg = this.hasOpenPaypalPopup();

            setTimeout(() => {
              this.updateD2dstate();
              const bookingProduct = bookingReducer.bookingDetails.bookingProduct !== 'abc' ? 'abc' : 'ccd';
              this._eventsService.goToBookingSwitcher(bookingProduct, 'payment');
            }, 30);

          } else {

            // complete booking
            if (product === 'abc') {
              ga('set', 'page', '/abc/complete');
              ga('send', 'pageview');
            } else if (product === 'ccd') {
              ga('set', 'page', '/ccd/complete');
              ga('send', 'pageview');
            }

            // this.loginNewUser();

            // const abandonmentRequest = {
            //   email: this.currentBooking.bookingUser.emailAddress
            // };

            // this._abandonmentService.setAbandonment(abandonmentRequest, 'Unsubscribe');
            this.bookingProduct = product;
            this._appStore.dispatch({ type: 'PAYMENT_WITH_SAVED_CARD', payload: !!modifiedRequest.makePaymentRequest.recurringPayment });
            this._appStore.dispatch({ type: 'UPDATE_CUSTOMER_RETURN_URL', payload: bookingResponse.returnJourneyURL });
            this._appStore.dispatch({ type: 'UPDATE_PNR_DATA', payload: bookingResponse.pnrBookingData });
            this.saveCutOffDateTime(bookingResponse.paymentResponse);
            setTimeout(() => {
              this.updateD2dstate();

              if (bookingResponse.paymentResponse.resultCode === 0 || bookingResponse.paymentResponse.resultCode === 1) {
                // 3DS authorized, complete booking
                this.handleBookingSuccess();

              } else if (bookingResponse.paymentResponse.resultCode === 7) {
                // this._appLoaderService.hideLoader();
                // this._errorService.handleCustomError({ text: "Unfortunately we can't accept this card please try another card or call customer services. United Kingdom: +44 203 384 6677, Switzerland: +41 800 837 732", sticky: true });
                // 3DS1 Redirection (also used for PayPal's ResultCode =
                // RedirectShopper - same response shape, different
                // rendering downstream).

                // Without this guard a missing/renamed redirectShopperTokenResponse
                // throws a TypeError inside this setTimeout, which is swallowed
                // silently - the user just sees nothing happen after the API call.
                if (!bookingResponse.paymentResponse.redirectShopperTokenResponse) {
                  console.error(
                    'resultCode 7 but redirectShopperTokenResponse is missing',
                    bookingResponse.paymentResponse
                  );
                  this._appLoaderService.hideLoader();
                  this._errorService.handleCustomError({
                    text: 'We could not start the payment redirect. Please try again or select another payment method.',
                    sticky: false
                  });
                  return;
                }

                const data = {
                  serverTransID: '',
                  acsTransID: '',
                  messageVersion: '',
                  acsURL: '',
                  paymentSessionId: bookingResponse.paymentResponse.redirectShopperTokenResponse.paymentSessionId,
                  paymentRequest: bookingResponse.paymentResponse.redirectShopperTokenResponse.paymentRequest,
                  termUrl: bookingResponse.paymentResponse.redirectShopperTokenResponse.termUrl,
                  issueUrl: bookingResponse.paymentResponse.redirectShopperTokenResponse.issuerUrl,
                  notificationUrl: bookingResponse.paymentResponse.redirectShopperTokenResponse.notificationUrl,
                  resultCode: 7,
                  isPaypal: !!isPaypal
                };

                this.initiateChallengeShopper(data);
              } else if (bookingResponse.paymentResponse.resultCode === 9) {
                // 3DS Identify Shopper
                const identifyRequest = {
                  threeDSServerTransID: bookingResponse.paymentResponse.tokenResponse.serverTransID,
                  threeDSMethodNotificationURL: bookingResponse.paymentResponse.tokenResponse.notificationURL
                };
                const encodedIdentifyRequest = ThreedDS2Utils.base64Url.encode(JSON.stringify(identifyRequest));

                const dataObj = {
                  request: encodedIdentifyRequest,
                  postURL: bookingResponse.paymentResponse.tokenResponse.notificationURL,
                  originURL: bookingResponse.paymentResponse.tokenResponse.notificationURLOrigin,
                  type: 'identify'
                };

                this._appStore.dispatch({ type: 'UPDATE_3DS_DATA', payload: dataObj });
              } else if (bookingResponse.paymentResponse.resultCode === 10) {
                // 3DS Challenge Shopper
                const data = {
                  serverTransID: bookingResponse.paymentResponse.tokenResponse.serverTransID,
                  acsTransID: bookingResponse.paymentResponse.tokenResponse.acsTransID,
                  messageVersion: bookingResponse.paymentResponse.tokenResponse.messageVersion,
                  acsURL: bookingResponse.paymentResponse.tokenResponse.acsURL
                };

                this.initiateChallengeShopper(data);
              } else if (this._paypalPopupCancelled) {
                // The customer cancelled the PayPal popup while this call was in
                // flight, and the result is not one the branches above handle
                // (e.g. a refusal). Nothing else will release the screen, so do
                // it here rather than leaving them stuck behind the loader.
                this._appLoaderService.hideLoader();
                this.paypalPopupAbandoned$.next({ requestStillPending: false });
              }

            }, 30);


            // this._router.navigate(['complete'], { queryParams: { ref: this.currentBooking.bookingReference } });
          }
        },
        err => {
          let errJson = null;

          if (err.error.bookingReference && err.error.bookingJourneyReference) {
            this.completeBookingRef = err.error.bookingReference;
            this.saveNewBookingReference(err.error);
          }
          console.log(err);
          try {
            errJson = err;
          } catch (cErr) {
            errJson = JSON.parse(err._body);
          }

          if (typeof errJson.returnJourneyURL !== 'undefined') {
            this._appStore.dispatch({ type: 'UPDATE_CUSTOMER_RETURN_URL', payload: errJson.returnJourneyURL });
          }

          this.exponeaPaymentErrorEvent(err);
          // The payment call failed outright, so any PayPal popup opened for
          // this attempt will never receive a redirect - close it rather than
          // leaving the customer on the placeholder.
          this.closePaypalPopup();
          this.bookingPaymentError$.next(true);
          this._errorService.handleError(err);

          this.loginNewUser();

          return;
        }
      );

  }

  // --- PayPal redirect popup -------------------------------------------------
  //
  // Adyen's PayPal RedirectShopper response only returns issuerUrl (a
  // checkoutPaymentRedirect link with all state pre-encoded in redirectData);
  // paymentSessionId/paymentRequest/termUrl are null, so unlike the card 3DS1
  // flow there is nothing to POST - it is a plain GET navigation. It also
  // cannot render in an iframe (X-Frame-Options on the approval page), hence a
  // popup window.

  // Opens the popup. MUST be called synchronously from the click handler,
  // before any HTTP round-trip, or the browser treats it as an unrequested
  // popup and blocks it. Returns false when it was blocked so the caller can
  // decide how to recover.
  openPaypalPlaceholderPopup(): boolean {
    if (!isPlatformBrowser(this.platformId)) { return false; }

    this._paypalRedirectNavigated = false;
    this._paypalPopupWasOpened = false;
    this._paypalPopupCancelled = false;
    this._paypalRedirectPopup = window.open('', 'paypalRedirectPopup', 'width=450,height=650');

    if (!this._paypalRedirectPopup) { return false; }

    this._paypalPopupWasOpened = true;
    this.writePaypalPopupPlaceholder();
    // Watch from the moment it opens, not just after the redirect: the customer
    // may close it while ADD_BOOKING is still in flight, and they shouldn't have
    // to wait for that response to find out the payment is off.
    this.startPaypalPopupWatch();
    return true;
  }

  // The popup is open for several seconds while ADD_BOOKING runs - and on a D2D
  // booking across the product switch and the second call too. Give it visible
  // content rather than a blank white window.
  private writePaypalPopupPlaceholder(): void {
    if (!this._paypalRedirectPopup) { return; }

    const copy = this.paypalPopupCopy();

    try {
      const doc = this._paypalRedirectPopup.document;
      doc.open();
      doc.write(`<!doctype html>
<html><head><meta charset="utf-8"><title>${copy.title}</title>
<style>
  body { margin:0; height:100vh; display:flex; align-items:center; justify-content:center;
         font-family:Arial,Helvetica,sans-serif; background:#f4f4f6; color:#2d2d3a; }
  .box { text-align:center; padding:24px; max-width:320px; }
  h1 { font-size:18px; margin:0 0 12px; }
  p { font-size:14px; line-height:1.5; margin:0 0 16px; color:#5a5a6e; }
  .spinner { width:28px; height:28px; margin:0 auto 20px; border:3px solid #d9d9e3;
             border-top-color:#6b4ee6; border-radius:50%; animation:spin 0.9s linear infinite; }
  @keyframes spin { to { transform:rotate(360deg); } }
</style></head>
<body><div class="box">
  <div class="spinner"></div>
  <h1>${copy.title}</h1>
  <p>${copy.redirecting}</p>
  <p><strong>${copy.keepOpen}</strong></p>
</div></body></html>`);
      doc.close();
    } catch (e) {
      // Best-effort only - a blank popup still completes the payment.
      console.log('PayPal popup placeholder could not be written', e);
    }
  }

  private paypalPopupCopy(): { title: string; redirecting: string; keepOpen: string } {
    const language = this.getState(this._appStore).copyReducer.language || '';

    const copy = {
      '': {
        title: 'Setting up your payment',
        redirecting: 'You\'ll be redirected to PayPal in a few seconds.',
        keepOpen: 'Please keep this window open.'
      },
      '_fr': {
        title: 'Configuration de votre paiement',
        redirecting: 'Vous serez redirig\u00e9 vers PayPal dans quelques secondes.',
        keepOpen: 'Veuillez laisser cette fen\u00eatre ouverte.'
      },
      '_de': {
        title: 'Einrichtung Ihrer Zahlung',
        redirecting: 'Sie werden in wenigen Sekunden zu PayPal weitergeleitet.',
        keepOpen: 'Bitte lassen Sie dieses Fenster ge\u00f6ffnet.'
      },
      '_it': {
        title: 'Configurazione del pagamento',
        redirecting: 'Tra pochi secondi verrai reindirizzato a PayPal.',
        keepOpen: 'Tieni aperta questa finestra.'
      }
    };

    return copy[language] || copy[''];
  }

  // True when a popup is open and still usable for a redirect.
  hasOpenPaypalPopup(): boolean {
    return !!this._paypalRedirectPopup && !this._paypalRedirectPopup.closed;
  }

  // True once the popup has actually been sent to PayPal.
  isPaypalPopupNavigated(): boolean {
    return this._paypalRedirectNavigated;
  }

  // True if a popup was opened for this attempt (even if the customer has since
  // closed it). False means it never appeared - i.e. the browser blocked it.
  wasPaypalPopupOpened(): boolean {
    return this._paypalPopupWasOpened;
  }

  // True if the customer closed the popup themselves during this attempt. A
  // redirect response arriving after that must be ignored, not redirected.
  wasPaypalPopupCancelled(): boolean {
    return this._paypalPopupCancelled;
  }

  // Sends the popup to PayPal. Returns false if there is no usable popup left
  // (blocked, or closed by the customer mid-flow) so the caller can surface the
  // right error - reopening from here would be outside a user gesture.
  navigatePaypalPopup(issuerUrl: string): boolean {
    if (!isPlatformBrowser(this.platformId)) { return false; }
    if (!issuerUrl) { return false; }
    if (!this.hasOpenPaypalPopup()) { return false; }

    this._paypalRedirectNavigated = true;
    this._paypalRedirectPopup.location.replace(issuerUrl);
    this._paypalRedirectPopup.focus();
    return true;
  }

  // The payment screen is blocked behind the loader from the moment the popup
  // opens - through the ADD_BOOKING round-trip and then while the customer is in
  // PayPal. If they close the popup at any point during that, nothing would ever
  // release the screen, so poll for it and emit.
  //
  // The emitted value says whether the payment request had already gone out and
  // come back (i.e. the popup had been redirected). When it had NOT, the booking
  // call is still in flight, so the screen must stay blocked until it returns -
  // otherwise the customer could re-submit a booking that is mid-creation.
  private startPaypalPopupWatch(): void {
    this.stopPaypalPopupWatch();

    this._paypalPopupWatch = setInterval(() => {
      // A completed payment clears the reference via closePaypalPopup() (which
      // also stops this watch), so if the reference is gone there is nothing to
      // report - only a window that is still referenced but closed means the
      // customer shut it themselves.
      if (!this._paypalRedirectPopup) {
        this.stopPaypalPopupWatch();
        return;
      }

      if (this._paypalRedirectPopup.closed) {
        const wasNavigated = this._paypalRedirectNavigated;
        this.closePaypalPopup(true);
        this._paypalPopupCancelled = true;
        this.paypalPopupAbandoned$.next({ requestStillPending: !wasNavigated });
      }
    }, 1000);
  }

  private stopPaypalPopupWatch(): void {
    if (this._paypalPopupWatch) {
      clearInterval(this._paypalPopupWatch);
      this._paypalPopupWatch = null;
    }
  }

  // Closes the popup. Used when the submit aborts at a validation guard, when
  // ADD_BOOKING/MakePayment errors, and when a redirect cannot be started.
  // Deliberately does NOT close a popup already sent to PayPal - that payment
  // is live. Safe to call when no popup is open.
  closePaypalPopup(force = false): void {
    if (this._paypalRedirectNavigated && !force) { return; }

    this.stopPaypalPopupWatch();

    if (this._paypalRedirectPopup && !this._paypalRedirectPopup.closed) {
      this._paypalRedirectPopup.close();
    }
    this._paypalRedirectPopup = null;
    this._paypalRedirectNavigated = false;
    this._paypalAwaitingD2dSecondLeg = false;
    this._paypalPopupWasOpened = false;
  }

  // Called when the payment component is destroyed. The popup should survive
  // exactly one destroy - the D2D product switch between the first (non-paying)
  // and second (paying) ADD_BOOKING calls. Any other destroy means the customer
  // left the payment screen (back to timeslots, etc.), so the popup would be
  // orphaned and must be closed.
  handlePaymentScreenLeft(): void {
    if (this._paypalAwaitingD2dSecondLeg) {
      // This destroy IS the D2D switch. Consume the allowance so a later
      // navigation away from the second leg still closes the popup.
      this._paypalAwaitingD2dSecondLeg = false;
      return;
    }

    // force: true because the customer has left the payment screen, so the
    // popup should go even if it was already sent to PayPal - abandoning the
    // screen abandons the payment.
    //
    // This also fires on the success path (handleBookingSuccess() navigates to
    // /complete), where closing any still-open PayPal window is what we want -
    // the payment has already been authorised and reported back by then.
    this.closePaypalPopup(true);
  }

  initiateChallengeShopper(data) {

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });

    if (data.resultCode && data.resultCode === 7) {
      const dataObj = {
        request: '',
        postURL: '',
        originURL: '',
        type: 'redirect',
        paymentSessionId: data.paymentSessionId,
        paymentRequest: data.paymentRequest,
        termUrl: data.termUrl,
        issueUrl: data.issueUrl,
        notificationUrl: data.notificationUrl,
        // PayPal's ResultCode = RedirectShopper reuses this exact redirect
        // shape - the marker tells the threeDS2Reducer subscriber whether
        // to render it as a hidden-iframe form submit (bank ACS pages, the
        // existing behavior) or a popup window (PayPal's own site, which
        // will not allow iframe embedding).
        isPaypal: !!data.isPaypal,
      };

      this._appStore.dispatch({ type: 'UPDATE_3DS_DATA', payload: dataObj });
    } else {
      // 3DS Challenge shopper
      const challengeRequest = {
        threeDSServerTransID: data.serverTransID,
        acsTransID: data.acsTransID,
        messageVersion: data.messageVersion,
        messageType: 'CReq',
        challengeWindowSize: '05'
      };

      const encodedChallengeRequest = ThreedDS2Utils.base64Url.encode(JSON.stringify(challengeRequest));

      const dataObj = {
        request: encodedChallengeRequest,
        postURL: data.acsURL,
        originURL: data.originURL,
        type: 'challenge'
      };

      this._appStore.dispatch({ type: 'UPDATE_3DS_DATA', payload: dataObj });
    }
  }

  handleBookingSuccess() {
    if (this.bookingProduct === 'abc') {
      ga('set', 'page', '/abc/complete');
      ga('send', 'pageview');
    } else if (this.bookingProduct === 'ccd') {
      ga('set', 'page', '/ccd/complete');
      ga('send', 'pageview');
    }

    this.loginNewUser();

    const abandonmentRequest = {
      email: this.currentBooking.bookingUser.emailAddress
    };

    this.handlePaymentSuccess();

    this._abandonmentService.setAbandonment(abandonmentRequest, 'Unsubscribe');
    const isFlyback = this.getState(this._appStore).flybackReducer.flybackBooking;
    if (isFlyback) {
      this._appStore.dispatch({ type: 'UPDATE_FLY_BACK_COMPLETE_STATUS', payload: true });
      this._appStore.dispatch({ type: 'UPDATE_FLYBACK_BOOKING_STATUS', payload: false });
      setTimeout(() => {
        this._router.navigate(['complete'], { queryParams: { ref: this.completeBookingRef || this.currentBooking.bookingReference } });
      }, 2500);
    } else {
      // check for flyback eligibility
      this.checkFlyBackEligibility().then((res: any) => {
        const state = this.getState(this._appStore);
        const d2dReducer = state.d2dReducer;
        this._appStore.dispatch({
          type: 'SAVE_D2D_BOOKINGS_DATA', payload: {
            d2dReducer: d2dReducer,
            bookingReducer: state.bookingReducer,
            flightFinderReducer: state.flightFinderReducer,
            milesAndMoreReducer: state.milesAndMoreReducer,
          }
        });
        this._dataStorageService.saveData();

        if (res.flyBackProducts && res.flyBackProducts.length > 0) {
          this._router.navigate(['fly-back-details'], { queryParams: { ref: this.completeBookingRef } });
        } else {
          setTimeout(() => {
            this._router.navigate(['complete'], { queryParams: { ref: this.completeBookingRef || this.currentBooking.bookingReference } });
          }, 250);
        }
      });
    }

  }

  handleMakePaymentSuccess(product, type) {

    this.handlePaymentSuccess();

    if (type === 'amend') {
      const messageData = {
        icon: 'tick_in_circle',
        sticky: false,
        type: 'success',
        text: 'Booking paid. You will be re-directed back to your account.'
      };

      this._errorService.handleCustomInfo(messageData);

      setTimeout(() => {
        this._router.navigate(['my-account/bookings/booking-details'], { queryParams: { ref: this.currentBooking.bookingReference } });
      }, 2500);
      return;

    } else {
      const abandonmentRequest = {
        email: this.currentBooking.bookingUser.emailAddress
      };

      this._abandonmentService.setAbandonment(abandonmentRequest, 'Unsubscribe');

      if (product === 'abc') {
        ga('set', 'page', '/abc/complete');
        ga('send', 'pageview');
      } else if (product === 'ccd') {
        ga('set', 'page', '/ccd/complete');
        ga('send', 'pageview');
      } else if (product === 'sds') {
        ga('set', 'page', '/sds/complete');
        ga('send', 'pageview');
      }

      const isFlyback = this.getState(this._appStore).flybackReducer.flybackBooking;
      if (isFlyback) {
        this._appStore.dispatch({ type: 'UPDATE_FLYBACK_BOOKING_STATUS', payload: false });
        this._appStore.dispatch({ type: 'UPDATE_FLY_BACK_COMPLETE_STATUS', payload: true });
        setTimeout(() => {
          this._router.navigate(['complete'], { queryParams: { ref: this.completeBookingRef || this.currentBooking.bookingReference } });
        }, 2500);
      } else {
        this.checkFlyBackEligibility().then((res: any) => {
          const state = this.getState(this._appStore);
          const d2dReducer = state.d2dReducer;
          this._appStore.dispatch({
            type: 'SAVE_D2D_BOOKINGS_DATA', payload: {
              d2dReducer: d2dReducer,
              bookingReducer: state.bookingReducer,
              flightFinderReducer: state.flightFinderReducer,
              milesAndMoreReducer: state.milesAndMoreReducer,
            }
          });
          this._dataStorageService.saveData();

          if (res.flyBackProducts && res.flyBackProducts.length > 0) {
            this._router.navigate(['fly-back-details'], { queryParams: { ref: this.completeBookingRef } });
          } else {
            this._router.navigate(['complete'], { queryParams: { ref: this.completeBookingRef || this.currentBooking.bookingReference } });
          }
        });
      }
    }

    // if (bookingResponse.validationErrorMessages !== null) {
    //   console.log(bookingResponse.validationErrorMessages);
    // } else {
    //   this._appStore.dispatch({ type: 'UPDATE_BOOKING_PAID', payload: true });
    //   this._router.navigate(['complete']);
    // }
  }

  getAmendBookingFlagValue(newFlag) {
    this._amendBookingFlag = newFlag;
    return this._amendBookingFlag;
  }

  formatCollectionPostLocation(data) {
    return {
      "type": 4,
      "name": data.name,
      "shortName": "",
      "geoCoord": data.geoCoord,
      "addressLine1": data.addressLine1,
      "addressLine2": data.addressLine2,
      "addressLine3": "",
      "addressTown": data.addressTown,
      "addressPostCode": data.addressPostCode,
      "addressCounty": data.addressCounty,
      "addressCountry": data.addressCountry
    };
  }

  // amends
  postAmend(data, storeData?, addBookingRequest?, referrer?: string, backupState?, expressModalOTP = false) {

    const amendFlag = this.getAmendBookingFlagValue(data.flag);

    const request: AmendRequest = {
      amendBookingFlag: amendFlag,
      bookingJourneyReference: data.reference,
      addBookingRequest: addBookingRequest ? addBookingRequest : this.currentBooking
    };

    if (referrer) {
      request.addBookingRequest.referrer = referrer;
    }
    // else {
    //   request.addBookingRequest.referrer = null;
    // }

    if (storeData) {
      this._appStore.dispatch({ type: 'UPDATE_AMEND_OBJECT', payload: request });
      return;
    }

    // const headers = new HttpHeaders();
    // headers.append('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);

    let headers = new HttpHeaders();
    headers = headers.set('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);
    headers = this.createAuthorizationHeader(headers, this._token);

    const state = backupState ? backupState : this.getState(this._appStore);
    const flightData = state.flightReducer.flightDetails.flightResponse;
    const ccd = state.bookingReducer.bookingDetails.bookingType === 'amend' ? state.bookingReducer.bookingDetails.bookingProduct : state.flightReducer.flightDetails.flightProducts.ccd;
    const flightDirection = state.flightReducer.flightDetails.flightResponse.flightDirection;
    // const majorPriceDifference = state.priceReducer.price.priceHistory.majorPriceDifference;

    const booking_user = state.currentBookingReducer.currentBooking.bookingUser;
    const cs_user = state.userReducer?.userDetails;

    const is_cs = cs_user?.userRole !== "CUSTOMER";

    let amendmentFlag = AMEND_MAPPING[amendFlag];

    let amendExponeaEventName = 'amend_summary';

    if (amendFlag === 64 && !referrer) {
      amendExponeaEventName = 'amend_customer_details';
      const bookerDataBackup = state.bookingReducer.bookingDetails.bookerDataBackup;

      if (is_cs && bookerDataBackup.emailAddress !== booking_user.emailAddress) {
        amendmentFlag = `email`;
      }

      if (bookerDataBackup.firstName !== booking_user.firstName) {
        amendmentFlag = amendmentFlag ? `${amendmentFlag}, first name` : 'first name';
      }

      if (bookerDataBackup.lastName !== booking_user.lastName) {
        amendmentFlag = amendmentFlag ? `${amendmentFlag}, last name` : 'last name';
      }

      if (bookerDataBackup.phone !== booking_user.phoneNumber || bookerDataBackup.countryCode !== booking_user.countryCode) {
        amendmentFlag = amendmentFlag ? `${amendmentFlag}, phone` : 'phone';
      }

    }

    // const ccd = true;
    let deliverDateTime;
    let collectionDateTime;
    let modifiedRequest = JSON.parse(JSON.stringify(request))
    if (ccd && flightDirection === 2) {
      const flightLegs = state.flightReducer.flightDetails.flightResponse.flightStatusDetails;
      collectionDateTime = flightLegs[flightLegs.length - 1].arrivalDateTimeDetails.scheduledDateTimeLocal;
      deliverDateTime = state.timeslotReducer.selectedTimeslotDateTime;
      if (deliverDateTime === null || deliverDateTime === '') {
        deliverDateTime = state.currentBookingReducer.currentBooking.bookingJourney[0].deliveryDateTimeUTC;
      }
      let bookingJourney = { ...modifiedRequest.addBookingRequest.bookingJourney[0] }
      const updatedBookingJourney = { ...bookingJourney, collectionDateTimeUTC: collectionDateTime, deliveryDateTimeUTC: deliverDateTime };
      modifiedRequest = {
        ...modifiedRequest,
        addBookingRequest: {
          ...modifiedRequest.addBookingRequest,
          bookingJourney: [updatedBookingJourney]
        }
      };

      // modifiedRequest.makePaymentRequest.flightDateTime = collectionDateTime;
      // modifiedRequest.addBookingRequest.bookingJourney[0].addOnProductType = [2];
      // if (deliverDateTime && !this.currentBooking.bookingJourney[0].deliveryDateTimeUTC) {
      //   modifiedRequest.bookingJourney[0].deliveryDateTimeUTC = deliverDateTime;
      // }
    }
    this.departureCountryCode = flightData.flightStatusDetails[0].departureAirport.airportCountryCode;
    this.arrivalCountryCode = flightData.flightStatusDetails[flightData.flightStatusDetails.length - 1].arrivalAirport.airportCountryCode;
    this.bookingProduct = state.bookingReducer.bookingDetails.bookingProduct;
    // const currency = (this.departureCountryCode === 'CH' && this.bookingProduct === 'abc') || (this.arrivalCountryCode === 'CH' && this.bookingProduct === 'ccd') ? 'CHF' : 'GBP';
    const currency = state.timeslotReducer.currency.code;

    modifiedRequest.addBookingRequest.makePaymentRequest.totalAmount.currency = currency;
    modifiedRequest.addBookingRequest.bookingZoneId = state.timeslotReducer.timeslotFormattedData ? state.timeslotReducer.timeslotFormattedData.bookingZoneId : '';
    // check if hubdrop
    if (flightDirection === 1 && state.hubDropReducer.selectedPostOffice && state.hubDropReducer.selectedCollectionType === 'atPostOffice') {
      modifiedRequest.addBookingRequest.bookingJourney[0].preAcceptanceType = 'HubDrop';
      // modifiedRequest.addBookingRequest.makePaymentRequest.shopperInfo.billingAddress = modifiedRequest.addBookingRequest.bookingJourney[0].collectionLocation;
      modifiedRequest.addBookingRequest.bookingJourney[0].collectionLocation = this.formatCollectionPostLocation(state.hubDropReducer.selectedPostOffice);
    }
    // if flight amended then send these attr into exponea
    let requestDepartureDate = '';
    let requestFlightNumber = '';
    if (state.bookingReducer.bookingDetails.bookingFlightDateAmend) {
      requestDepartureDate = moment(modifiedRequest.addBookingRequest.bookingJourney[0].flightData.requestDepartureDate).format('DD/MM/YYYY');
      requestFlightNumber = modifiedRequest.addBookingRequest.bookingJourney[0].flightData.requestFlightNumber;

    }
    this._appLoaderService.showLoader('Booking reference: ' + modifiedRequest.addBookingRequest.bookingReference)

    // if(modifiedRequest.addBookingRequest.makePaymentRequest.totalAmount.amount === 0){
    //   modifiedRequest.addBookingRequest.makePaymentRequest = null;
    // }

    modifiedRequest.slotNumber = this._sharedService.getCurrentSlotNumber();;

    this._http
      .post(API_PATH + AMEND_BOOKING, modifiedRequest, { headers: headers })
      .subscribe(
        (res: any) => {
          // A PayPal amend continues in the popup after this response, so the
          // screen must stay blocked until that reports back - hiding the loader
          // would also re-enable the submit button. Every other amend keeps its
          // long-standing behaviour of releasing the screen here.
          const isPaypalAmend = !!modifiedRequest.addBookingRequest?.makePaymentRequest?.paypalPayment
            && res?.paymentResponse?.resultCode === 7;

          if (!isPaypalAmend) {
            this._appLoaderService.hideLoader();
          }

          if (referrer === 'express-checkout-comms-pref') {

            const bookingUser = modifiedRequest.addBookingRequest.bookingUser;

            const googlePayToken = this.getState(this._appStore).d2dReducer.googlePayToken;
            const applePayToken = this.getState(this._appStore).d2dReducer.applePayToken;

            let user_old_email = '';
            let user_old_phonenumber = '';

            if (googlePayToken) {
              user_old_email = googlePayToken.email;
              const { countryCode, phone } = this.parsePhoneNumber(googlePayToken.shippingAddress.phoneNumber);
              user_old_phonenumber = countryCode + phone;
            } else if (applePayToken) {
              user_old_email = applePayToken.shippingContact.emailAddress;
              const { countryCode, phone } = this.parsePhoneNumber(applePayToken.shippingContact.phoneNumber);
              user_old_phonenumber = countryCode + phone;
            }

            exponea.track('comms_preferences', {
              expressModalOTP: expressModalOTP,
              old_email: user_old_email,
              updatedEmail: user_old_email === bookingUser.emailAddress ? 'No' : 'Yes',
              updatedPhone: user_old_phonenumber === bookingUser.countryCode + bookingUser.phoneNumber ? 'No' : 'Yes',
              entry_source: this.getState(this._appStore).d2dReducer.entrySource || 'direct',
            });

            this.expressCheckoutCommsPref$.next('remove');
            return;
          }

          if (referrer) {
            this._errorService.removeModal();
            return;
          }
          const isCustomer = state.userReducer.userDetails.userRole === 'CUSTOMER';
          if (!res.paymentResponse) {
            this.handleAmendSuccess();
            const eventAttributes: any = {
              email: booking_user.emailAddress,
              cs_amended: is_cs,
              cs_name: is_cs ? `${cs_user.currentUser.firstName} ${cs_user.currentUser.lastName}` : '',
              cs_email: is_cs ? cs_user.currentUser.emailAddress : '',
              amendment_flag: amendmentFlag,
              amend_failed: 'No',
              fail_reason: '',
              date: requestDepartureDate,
              flight_number: requestFlightNumber,
              phone_number: '+' + booking_user.countryCode + booking_user.phoneNumber,
              entry_source: this.getState(this._appStore).d2dReducer.entrySource || 'direct',
            };
            exponea.track(amendExponeaEventName, eventAttributes);
          } else {
            const bookingResponse = res;

            if (bookingResponse.paymentResponse.resultCode === 0 || bookingResponse.paymentResponse.resultCode === 1) {
              // 3DS authorized, complete booking
              this.handleAmendSuccess();
              const eventAttributes: any = {
                email: booking_user.emailAddress,
                cs_amended: is_cs,
                cs_name: is_cs ? `${cs_user.currentUser.firstName} ${cs_user.currentUser.lastName}` : '',
                cs_email: is_cs ? cs_user.currentUser.emailAddress : '',
                amendment_flag: amendmentFlag,
                amend_failed: 'No',
                fail_reason: '',
                date: requestDepartureDate,
                flight_number: requestFlightNumber,
                phone_number: '+' + booking_user.countryCode + booking_user.phoneNumber,
                entry_source: this.getState(this._appStore).d2dReducer.entrySource || 'direct',
              };
              exponea.track(amendExponeaEventName, eventAttributes);

            } else if (bookingResponse.paymentResponse.resultCode === 7) {
              // this._appLoaderService.hideLoader();
              // this._errorService.handleCustomError({ text: "Unfortunately we can't accept this card please try another card or call customer services. United Kingdom: +44 203 384 6677, Switzerland: +41 800 837 732", sticky: true });

              // 3DS1 Redirection (also used for PayPal's ResultCode =
              // RedirectShopper - same response shape, different
              // rendering downstream).

              // Without this guard a missing/renamed redirectShopperTokenResponse
              // throws a TypeError that is swallowed, leaving the customer with
              // nothing happening after the API call.
              if (!bookingResponse.paymentResponse.redirectShopperTokenResponse) {
                console.error(
                  'amend: resultCode 7 but redirectShopperTokenResponse is missing',
                  bookingResponse.paymentResponse
                );
                this.closePaypalPopup();
                this._appLoaderService.hideLoader();
                this._errorService.handleCustomError({
                  text: 'We could not start the payment redirect. Please try again or select another payment method.',
                  sticky: false
                });
                return;
              }

              const data = {
                serverTransID: '',
                acsTransID: '',
                messageVersion: '',
                acsURL: '',
                paymentSessionId: bookingResponse.paymentResponse.redirectShopperTokenResponse.paymentSessionId,
                paymentRequest: bookingResponse.paymentResponse.redirectShopperTokenResponse.paymentRequest,
                termUrl: bookingResponse.paymentResponse.redirectShopperTokenResponse.termUrl,
                issueUrl: bookingResponse.paymentResponse.redirectShopperTokenResponse.issuerUrl,
                notificationUrl: bookingResponse.paymentResponse.redirectShopperTokenResponse.notificationUrl,
                resultCode: 7,
                isPaypal: !!modifiedRequest.addBookingRequest.makePaymentRequest?.paypalPayment
              };

              this.initiateChallengeShopper(data);

              // exponea.track(amendExponeaEventName, {
              //   email : booking_user.emailAddress,
              //   cs_amended : is_cs,
              //   cs_name : is_cs ? `${cs_user.currentUser.firstName} ${cs_user.currentUser.lastName}`: '',
              //   cs_email : is_cs ? cs_user.currentUser.emailAddress : '',
              //   amendment_flag: amendmentFlag,
              //   amend_failed: 'Yes',
              //   fail_reason: "Unfortunately we can't accept this card please try another card or call customer services. United Kingdom: +44 203 384 6677, Switzerland: +41 800 837 732"
              // });

            } else if (bookingResponse.paymentResponse.resultCode === 9) {
              // 3DS Identify Shopper
              const identifyRequest = {
                threeDSServerTransID: bookingResponse.paymentResponse.tokenResponse.serverTransID,
                threeDSMethodNotificationURL: bookingResponse.paymentResponse.tokenResponse.notificationURL
              };
              const encodedIdentifyRequest = ThreedDS2Utils.base64Url.encode(JSON.stringify(identifyRequest));

              const dataObj = {
                request: encodedIdentifyRequest,
                postURL: bookingResponse.paymentResponse.tokenResponse.notificationURL,
                originURL: bookingResponse.paymentResponse.tokenResponse.notificationURLOrigin,
                type: 'identify'
              };

              this._appStore.dispatch({ type: 'UPDATE_3DS_DATA', payload: dataObj });
            } else if (bookingResponse.paymentResponse.resultCode === 10) {
              // 3DS Challenge Shopper
              const data = {
                serverTransID: bookingResponse.paymentResponse.tokenResponse.serverTransID,
                acsTransID: bookingResponse.paymentResponse.tokenResponse.acsTransID,
                messageVersion: bookingResponse.paymentResponse.tokenResponse.messageVersion,
                acsURL: bookingResponse.paymentResponse.tokenResponse.acsURL
              };

              this.initiateChallengeShopper(data);
            } else if (this._paypalPopupCancelled) {
              // The customer cancelled the PayPal popup while this amend was in
              // flight and the result is not one the branches above handle (e.g.
              // a refusal). Nothing else will release the screen.
              this._appLoaderService.hideLoader();
              this.paypalPopupAbandoned$.next({ requestStillPending: false });
            }
          }
        },
        err => {
          // if payment fails on amend
          // set flag to call makePayment on next try
          this._appLoaderService.hideLoader();

          // The amend call failed outright, so any PayPal popup opened for this
          // attempt will never receive a redirect - close it rather than leaving
          // the customer on the placeholder.
          this.closePaypalPopup();

          if (referrer === 'express-checkout-comms-pref') {
            this.expressCheckoutCommsPref$.next({ error: true, message: err.error.validationErrors[0] });
            return;
          }

          if (referrer) { return; }
          if (err.error.paymentResponse !== null) {
            if (!err.error.paymentResponse.isSuccessful) {
              this._appStore.dispatch({ type: 'UPDATE_BOOKING_AMEND_PAYMENT_FAILED', payload: true });
            }
          }
          this._errorService.handleError(err);
          const eventAttributes: any = {
            email: booking_user.emailAddress,
            cs_amended: is_cs,
            cs_name: is_cs ? `${cs_user.currentUser.firstName} ${cs_user.currentUser.lastName}` : '',
            cs_email: is_cs ? cs_user.currentUser.emailAddress : '',
            amendment_flag: amendmentFlag,
            amend_failed: 'Yes',
            fail_reason: JSON.stringify(err),
            date: requestDepartureDate,
            flight_number: requestFlightNumber,
            phone_number: '+' + booking_user.countryCode + booking_user.phoneNumber,
            entry_source: this.getState(this._appStore).d2dReducer.entrySource || 'direct',
          };

          exponea.track(amendExponeaEventName, eventAttributes);

          return;
        });

  }

  parsePhoneNumber(phoneNumber) {
    try {
      const phoneNumberObj = libphonenumber.parsePhoneNumber(phoneNumber);
      const countryCode = phoneNumberObj.countryCallingCode;
      const nationalNumber = phoneNumberObj.nationalNumber;
      return {
        countryCode: countryCode,
        phone: nationalNumber
      };
    } catch (e) {
      console.error('Error parsing phone number:', e);
      return {
        countryCode: '',
        phone: phoneNumber
      };
    }
  }

  handleAmendSuccess() {
    const messageData = {
      icon: 'alert',
      sticky: false,
      type: 'info',
      text: 'Your booking has been saved. You will be re-directed back to booking details'
    };

    this._errorService.handleCustomInfo(messageData);

    this.handlePaymentSuccess();

    // setTimeout(() => {
    this._router.navigate(['my-account/bookings/booking-details'], { queryParams: { ref: this.currentBooking.bookingReference } });
    // }, 2500);
  }

  handlePaymentSuccess() {
    // The PayPal flows keep the loader up past the API response (the payment is
    // still running in the popup), so release it once the payment has succeeded.
    // loaderState$ is a BehaviorSubject, so a lingering show:true would re-render
    // the loader on the next screen that hosts <app-page-loader> - which is what
    // happened on amend, where the flow returns to booking-details. Other payment
    // methods have already hidden it by now, making this a harmless no-op.
    this._appLoaderService.hideLoader();

    const d2dReducer = this.getState(this._appStore).d2dReducer;
    const applePayData = this.getApplePaymentToken();

    console.log('Payment successfull', applePayData, d2dReducer);

    if (applePayData && d2dReducer.paymentType === 1) {
      this.applePaymentComplete$.next(true);
    }

    // TODO: for GPay if needed
  }

  loginNewUser() {

    const newUser = this.getState(this._appStore).userReducer.userDetails.newUser;

    if (newUser !== null) {
      this._loginService.setPassword(newUser.emailAddress, newUser.password);
    }
  }

  exponeaPaymentErrorEvent(err) {
    let errJson = null;
    const sectionCode = [];
    let messageTextHolder = '';
    const state = this.getState(this._appStore);
    let payment_type = 'Card';
    if (state.d2dReducer.paymentType === 1 && state.d2dReducer.applePayToken) {
      payment_type = 'Apple Pay';
    } else if (state.d2dReducer.paymentType === 0 && state.d2dReducer.googlePayToken) {
      payment_type = 'Google Pay';
    } else if (state.d2dReducer.paymentType === 3) {
      payment_type = 'PayPal';
    }
    const saved_card = payment_type === 'Card' ? state.d2dReducer.paymentWithSavedCard : false;
    let card_type = payment_type === 'Card' ? state.d2dReducer.cardType : '';
    const logged_in = state.userReducer.userDetails.userLoggedIn;
    const email = state.userReducer.userDetails.userEmail;

    const promo_type = state.d2dReducer.promoDetails.promotType;

    let promo_amount = state.d2dReducer.promoDetails.promoAmount;
    if (promo_type && promo_type === 2 && promo_amount === 100 && !saved_card) {
      card_type = '';
      payment_type = '';
    }

    try {
      errJson = err.error;
    } catch (cErr) {
      errJson = JSON.parse(err._body);
    }

    if (err.status >= 400 && err.status < 500) {
      if (errJson.validationErrorMessages && typeof errJson.validationErrorMessages !== 'undefined') {
        for (const item of errJson.validationErrorMessages) {
          const sectionReason = item.reasons;
          sectionCode.push(item.code);
          for (const reason of sectionReason) {
            messageTextHolder += reason + '<br>';
          }
        }

        if (errJson.translationResponse?.translationValidationErrors?.length > 0) {
          messageTextHolder = '';
        }
        sectionCode.push(errJson.translationResponse.code)
        for (const err of errJson.translationResponse?.translationValidationErrors) {
          messageTextHolder += err.errorMessage + '<br>';
          const changeAbles = Object.keys(err.parameters);
          if (changeAbles.length > 0) {
            for (const params of changeAbles) {
              const text = '{' + params + '}';
              const params_text = err.parameters[params];
              messageTextHolder = messageTextHolder.replace(text, params_text)
            }
          }
        }

      } else if (typeof errJson.validationErrors !== 'undefined' && typeof errJson.translationResponse !== 'undefined') {
        for (const err of errJson.validationErrors) {
          messageTextHolder += err.errorMessage + '<br>';
        }

      } else if (typeof errJson.modelState !== 'undefined') {
        const model = errJson.modelState,
          modelCode = [],
          modelText = [];

        for (let h = 0; h < Object.keys(model).length; h++) {
          const prop = model[Object.keys(model)[h]];
          // split string on :
          const code = prop[0].split(':');
          // parse and push into array
          modelCode.push(parseInt(code[0], 10));
          modelText.push(code[1]);
        }
        if (typeof modelText[0] !== 'undefined') {
          messageTextHolder = modelText[0];
        } else {
          messageTextHolder = '030 There seems to have been an issue. If the problem persists please email: <a href="mailto:support@portr.com">support@portr.com</a>';
          if (errJson.message) {
            messageTextHolder = errJson.message;
          }
        }

      } else if (typeof errJson.error_description !== 'undefined') {
        messageTextHolder = errJson.error_description

      } else if (typeof errJson.validationErrors !== 'undefined') {
        for (const item of errJson.validationErrors) {
          messageTextHolder = item;
        }

      } else if (typeof errJson.description !== 'undefined') {
        messageTextHolder = errJson.description;
      } else {
        messageTextHolder = 'There seems to have been an issue. If the problem persists please email: <a href="mailto:support@portr.com">support@portr.com</a>';
      }

      const bookingDetails = state.bookingReducer.bookingDetails;
      const currency = state.timeslotReducer.currency;
      exponea.track('payment_error', {
        booking_reference: bookingDetails.bookingReference,
        currency: currency.code,
        item_value_gross: bookingDetails.totalPrice,
        reason_forerror: messageTextHolder,
        payment_type,
        card_type,
        saved_card,
        logged_in,
        email,
        entry_source: this.getState(this._appStore).d2dReducer.entrySource || 'direct',
      });
    } else if (err.status === 500) {
      const bookingDetails = state.bookingReducer.bookingDetails;
      const currency = state.timeslotReducer.currency;
      exponea.track('payment_error', {
        booking_reference: bookingDetails.bookingReference,
        currency: currency.code,
        item_value_gross: bookingDetails.totalPrice,
        reason_forerror: '500 error',
        payment_type,
        card_type,
        saved_card,
        logged_in,
        email,
        entry_source: this.getState(this._appStore).d2dReducer.entrySource || 'direct',
      });
    }
  }


  // apple pay

  validateApplePayMerchant(url: string, callback: (value: Object) => void) {

    const headers = new HttpHeaders()
      .set('Ocp-Apim-Subscription-Key', SUBSCRIPTION_KEY);

    this._http
      .post(API_PATH + GET_APPLE_PAY_SESSION, { validationURL: url }, { headers: headers })
      .subscribe(
        (res) => callback(res),
        err => {
          this._errorService.handleError(err);
          return;
        });
  }

  saveNewBookingReference(bookingResponse) {
    this.updateBookingReference(bookingResponse.bookingReference);
    this.updateBookingJourneyReference(bookingResponse.bookingJourneyReference);
    this._appStore.dispatch({ type: 'UPDATE_BOOKING_REFERENCE', payload: bookingResponse.bookingReference });
    this._appStore.dispatch({ type: 'UPDATE_BOOKING_JOURNEY_REFERENCE', payload: bookingResponse.bookingJourneyReference });
  }

  resetForFlyback() {
    this.currentBooking = initialBooking;
    this._bookingOriginal = Object.assign({}, this.currentBooking);

    this._currentBookingJourney = initialBookingJourney;
    this._bookingJourneyOriginal = Object.assign({}, this._currentBookingJourney);

    this._currentBookingFlightData = initialBookingFlightData;
    this._bookingFlightOriginal = Object.assign({}, this._currentBookingFlightData);

    this._currentBookingDeliveryLocation = initialDeliveryLocation;
    this._bookingDeliveryLocationOriginal = Object.assign({}, this._currentBookingDeliveryLocation);

    this._currentBookingCollectionLocation = initialCollectionLocation;
    this._bookingCollectionLocationOriginal = Object.assign({}, this._currentBookingCollectionLocation);

    this._currentBookingMakePayment = initialMakePaymentRequest;
    this._bookingMakePaymentOriginal = Object.assign({}, this._currentBookingMakePayment);
  }

  setMilesPromotion(_priceService) {
    const currentDate = moment().set({
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0
    });
    if (currentDate.isBetween(this._milePromotionStartDate, this._milesPromotionEndDate, undefined, '[]')) {
      console.log('MilesJulyDiscount promo applied');
      this._appStore.dispatch({ type: 'UPDATE_PROMO', payload: 'MilesJulyDiscount' });
      setTimeout(() => {
        _priceService.getPrice();
      }, 500);
    }
  }
};
